// src/components/admin/page-sections/ProductCatalogVerticalSection.js
import Lottie from "lottie-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import cartAnimation from "../../../assets/cartAnimation.json";
import { useAuth } from "../../../context/AuthContext";
import { useCart } from "../../../context/CartContext";
import axios from "../../../utils/axiosHandler";

const ProductCatalogVerticalSection = ({ content }) => {
  const {
    title = "Browse Categories",
    description = "",
    searchEnabled = true,
    searchPlaceholder = "Search categories...",
    layoutType = "vertical", // 'vertical' or 'slider'
    sliderThreshold = 9, // Switch to slider if more categories than this
    categoryImageShape = "circle", // 'circle' or 'square'
    categoryBorderColor = "#3b82f6", // blue-500
    selectedCategoryBorderColor = "#3b82f6", // blue-400
    categoryBackgroundColor = "#f3f4f6", // gray-100
    selectedCategoryBackgroundColor = "#60a5fa", // blue-400
    categoryTextColor = "#1f2937", // gray-800
    selectedCategoryTextColor = "#ffffff", // white
    categoryHoverBackgroundColor = "#e5e7eb", // gray-200
    categoryHoverTextColor = "#111827", // gray-900
    loadMoreButtonText = "Load More",
    loadMoreButtonColor = "#1e3a8a", // blue-900
    loadIncrementDesktop = 6,
    loadIncrementMobile = 3,
    showLoadingAnimation = true,
    loadingAnimationUrl = "../images/loading.gif",
    apiPath = "/api/categories/basic",
    productApiPath = "/api/products/basic",
    productGridColumnsResponsive = {
      mobile: 1,
      tablet: 2,
      desktop: 3,
    },
    verticalImageSize = "medium", // 'small', 'medium', 'large'
    sliderImageSize = "medium", // 'small', 'medium', 'large'
    fadeEffectEnabled = true,
    productCardStyle = "shadow",
    showQuickAction = true,
    quickActionIcon = "customize",
    backgroundColor = "#F9FAFB",
    // Product display options
    productDisplayFields = {
      showName: true,
      showPrice: true,
      showDescription: false,
      showNote: false,
      showRating: false,
    },
    productInfoPosition = "below",
    productTextAlignment = "left",
    productNameLines = 2,
    productDescriptionLines = 3,
    productImageAspectRatio = "square",
    productClickBehavior = "navigate",
    productPopupSettings = {
      showGallery: true,
      showDescription: true,
      showPrice: true,
      showNote: true,
      showAttributes: true,
      showAddToCart: true,
      showCustomizeButton: true,
      popupSize: "medium",
    },

    //category
    showAllCategories = true,
    selectedCategoryIds = [],
    showAllParentCategories = true,
    selectedParentCategoryIds = [],
    parentCategoryDisplayStyle = "nameAndImage",

    //pricing & add to cart
    priceDisplayType = "regular",
    allowQuantitySelection = false,
    defaultQuantity = 1,
    priceVisibility = "all",
    cartVisibility = "all",
    loginPromptMessage = "Please login to see prices and purchase products",
  } = content;

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [visibleCount, setVisibleCount] = useState(
    window.innerWidth < 768 ? loadIncrementMobile : loadIncrementDesktop
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showFullPageLoading, setShowFullPageLoading] = useState(false);
  const categorySliderRef = useRef(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductPopup, setShowProductPopup] = useState(false);
  const [showCartNotification, setShowCartNotification] = useState(false);
  const cartContext = useCart();
  const [productQuantities, setProductQuantities] = useState({});
  const auth = useAuth();
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [parentCategories, setParentCategories] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const addToCart =
    cartContext?.addToCart ||
    ((product) => {
      console.log("Cart not available, would add:", product);
      // Still show notification for demo purposes
      setShowCartNotification(true);
      setTimeout(() => setShowCartNotification(false), 2000);
    });

  // Image size classes
  const verticalImageSizes = {
    small: "w-16 h-16",
    medium: "w-32 h-32",
    large: "w-40 h-40",
  };

  const sliderImageSizes = {
    small: "w-32 h-32",
    medium: "w-40 h-40",
    large: "w-48 h-48",
  };

  // Product card style options
  const cardStyles = {
    shadow: "shadow-lg",
    outline: "border border-gray-200",
    flat: "",
  };

  // Product image aspect ratio styles
  const productImageRatios = {
    square: "aspect-square",
    portrait: "aspect-[3/4]",
    landscape: "aspect-[4/3]",
    original: "aspect-auto",
  };

  // Product info position layout styles
  const productInfoStyles = {
    below: "flex flex-col",
    right: "flex flex-row",
    left: "flex flex-row-reverse",
    overlay: "relative",
  };

  // Add this to the useEffect that fetches categories data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // First fetch basic data without images (fast)
        const categoriesPromise = axios.get(apiPath).then((res) => res.data);
        const productsPromise = axios.get(productApiPath).then((res) => res.data);

        // Add this to fetch parent categories when needed
        const promises = [categoriesPromise, productsPromise];
        if (content.showParentCategories) {
          // Fetch all parent categories if showAllParentCategories is true,
          // otherwise only fetch selected ones
          const parentCategoryUrl = content.showAllParentCategories
            ? "/api/parent-categories"
            : `/api/parent-categories/with-images?ids=${encodeURIComponent(
              JSON.stringify(content.selectedParentCategoryIds || [])
            )}`;

          promises.push(axios.get(parentCategoryUrl).then((res) => res.data));
        }

        const results = await Promise.all(promises);
        const categoriesRes = results[0];
        const productsRes = results[1];

        // Process parent categories if needed
        if (content.showParentCategories && results.length > 2) {
          setParentCategories(results[2]);
        }

        // Rest of your existing code...
        setCategories(
          categoriesRes.sort((a, b) => {
            if (a.order !== undefined && b.order !== undefined) {
              return a.order - b.order;
            } else if (a.order !== undefined) {
              return -1;
            } else if (b.order !== undefined) {
              return 1;
            } else {
              return a.name.localeCompare(b.name);
            }
          })
        );

        setProducts(productsRes);
        setLoading(false);

        // Fetch images as before...
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [apiPath, productApiPath, content.showParentCategories]);

  // Add this helper function to get child categories for a specific parent
  const getCategoriesByParent = (parentId) => {
    return categories.filter(
      (category) =>
        category.parentCategory &&
        (category.parentCategory._id === parentId ||
          (typeof category.parentCategory === "string" &&
            category.parentCategory === parentId))
    );
  };

  // Set a default selected category if none is set and categories are loaded
  useEffect(() => {
    if (
      !selectedCategory &&
      categories.length > 0 &&
      window.innerWidth >= 768
    ) {
      // Find first category that's in the selected categories list (if applicable)
      let categoryToSelect = categories[0];
      if (!showAllCategories && selectedCategoryIds.length > 0) {
        const firstAvailableCategory = categories.find((cat) =>
          selectedCategoryIds.includes(cat._id)
        );
        if (firstAvailableCategory) {
          categoryToSelect = firstAvailableCategory;
        }
      }
      setSelectedCategory(categoryToSelect._id);
    }
  }, [selectedCategory, categories, showAllCategories, selectedCategoryIds]);

  // Reset visible count when a new category is selected
  useEffect(() => {
    setVisibleCount(
      window.innerWidth < 768 ? loadIncrementMobile : loadIncrementDesktop
    );
  }, [selectedCategory, loadIncrementMobile, loadIncrementDesktop]);

  // useEffect to initialize quantities
  useEffect(() => {
    // Initialize with default quantities when products load
    if (products.length > 0) {
      const initialQuantities = {};
      products.forEach((product) => {
        let defaultQty = defaultQuantity;
        if (priceDisplayType === "wholesale" && !defaultQty) {
          defaultQty = 50;
        } else if (priceDisplayType === "regular" && !defaultQty) {
          defaultQty = 1;
        }
        initialQuantities[product._id] = defaultQty;
      });
      setProductQuantities(initialQuantities);
    }
  }, [products, defaultQuantity, priceDisplayType]);

  // Function to handle product click
  const handleProductClick = (product) => {
    if (productClickBehavior === "popup") {
      setSelectedProduct(product);
      setShowProductPopup(true);
    } else {
      navigateToProduct(product);
    }
  };

  // Navigate to product detail page
  const navigateToProduct = (product) => {
    const match = product.name.match(/^(.*?)(\d.*)?$/);
    let baseName = match[1] ? match[1].trim() : product.name;
    let sizePart = match[2] ? match[2].trim() : "";

    const slugBase = baseName
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

    const slugSize = sizePart;
    const productSlug = slugSize ? `${slugBase}-${slugSize}` : slugBase;

    window.location.href = `/product/${productSlug}`;
  };

  // Function to check if user can see prices
  const canSeePrice = () => {
    if (priceVisibility === "all") return true;
    if (!auth.user) return false; // Not logged in
    if (priceVisibility === "authenticated") return true; // Any logged in user
    if (priceVisibility === "business")
      return auth.user.userType === "business";
    if (priceVisibility === "regular") return auth.user.userType === "regular";
    return false; // 'none' or any other value
  };

  // Function to check if user can add to cart
  const canAddToCart = () => {
    if (cartVisibility === "all") return true;
    if (!auth.user) return false; // Not logged in
    if (cartVisibility === "authenticated") return true; // Any logged in user
    if (cartVisibility === "business") return auth.user.userType === "business";
    if (cartVisibility === "regular") return auth.user.userType === "regular";
    return false; // 'none' or any other value
  };

  // Function to handle unauthorized cart actions
  const handleUnauthorizedCartAction = () => {
    setShowLoginPrompt(true);
    setTimeout(() => setShowLoginPrompt(false), 3000);
  };

  // Scroll horizontally in the category slider
  const scroll = (direction) => {
    const container = categorySliderRef.current;
    if (container) {
      const scrollAmount = direction === "left" ? -300 : 300;
      container.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  // Add product to cart
  const handleAddToCart = async (product) => {
    try {
      if (!canAddToCart()) {
        handleUnauthorizedCartAction();
        return;
      }

      // const token = localStorage.getItem("token");
      // if (!token) {
      //   window.location.href = "/login";
      //   return;
      // }

      // Use the selected quantity or default
      const quantity = productQuantities[product._id] || defaultQuantity || 1;

      await addToCart({
        product,
        quantity: quantity,
        customization: {
          preview: product.images[0]?.data || "",
          description: "",
        },
      });

      // Show notification and scroll to top
      setShowCartNotification(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => setShowCartNotification(false), 2000);
    } catch (error) {
      console.error("Error adding to cart:", error);
    }
  };

  // Fetch categories and products data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // First fetch basic data without images (fast)
        const [categoriesRes, productsRes] = await Promise.all([
          axios.get(apiPath).then((res) => res.data),
          axios.get(productApiPath).then((res) => res.data),
        ]);

        // Sort categories by order if available
        // setCategories(categoriesRes.sort((a, b) => a.order - b.order));
        setCategories(
          categoriesRes.sort((a, b) => {
            // If both have order property, sort by order
            if (a.order !== undefined && b.order !== undefined) {
              return a.order - b.order;
            }
            // If only a has order, it comes first
            else if (a.order !== undefined) {
              return -1;
            }
            // If only b has order, it comes first
            else if (b.order !== undefined) {
              return 1;
            }
            // If neither has order, sort by name
            else {
              return a.name.localeCompare(b.name);
            }
          })
        );

        setProducts(productsRes);
        setLoading(false);

        // Then fetch images in parallel
        const productImagePromises = productsRes.map((product) =>
          axios.get(`/api/products/${product._id}/image`)
            .then((res) => res.data)
            .then((imageData) => {
              setProducts((prev) =>
                prev.map((p) =>
                  p._id === product._id ? { ...p, images: [imageData] } : p
                )
              );
            })
            .catch((error) =>
              console.error(
                `Error loading image for product ${product._id}:`,
                error
              )
            )
        );

        const categoryImagePromises = categoriesRes.map((category) =>
          axios.get(`/api/categories/${category._id}/image`)
            .then((res) => res.data)
            .then((imageData) => {
              setCategories((prev) =>
                prev.map((c) =>
                  c._id === category._id ? { ...c, image: imageData } : c
                )
              );
            })
            .catch((error) =>
              console.error(
                `Error loading image for category ${category._id}:`,
                error
              )
            )
        );

        // Load all images in parallel
        await Promise.allSettled([
          ...productImagePromises,
          ...categoryImagePromises,
        ]);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [apiPath, productApiPath]);

  // Filter categories based on search input
  // const filteredCategories = categories.filter(category =>
  //   category.name.toLowerCase().includes(categorySearchTerm.toLowerCase())
  // );

  // const filteredCategories = [...categories].filter(category =>
  //   category.name.toLowerCase().includes(categorySearchTerm.toLowerCase())
  // );
  const filteredCategories = [...categories].filter(
    (category) =>
      // Filter by search term
      category.name.toLowerCase().includes(categorySearchTerm.toLowerCase()) &&
      // Filter by selected categories if showAllCategories is false
      (showAllCategories || selectedCategoryIds.includes(category._id))
  );

  // Scroll to category element after loading (mobile view)
  const scrollToCategory = (categoryId) => {
    setTimeout(() => {
      const element = document.getElementById(`category-${categoryId}`);
      if (element) {
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - 60; // 60px offset from top
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  // Calculate grid column classes
  const getGridColumns = () => {
    const mobile = productGridColumnsResponsive?.mobile || 1;
    const tablet = productGridColumnsResponsive?.tablet || 2;
    const desktop = productGridColumnsResponsive?.desktop || 3;

    return `grid-cols-${mobile} sm:grid-cols-${tablet} lg:grid-cols-${desktop}`;
  };

  return (
    <div
      className="container mx-auto px-4 pt-24 mb-32 min-h-screen"
      style={{ backgroundColor }}
    >
      {/* Loading indicator */}
      {loading && (
        // <div className="text-center py-12">
        //   <p className="text-xl text-gray-600">Loading products...</p>
        // </div>
        //<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
        <div className="fixed inset-0 flex flex-col items-center justify-center">
          <Lottie
            animationData={require("../../../assets/loading-product.json")}
            loop={true}
            className="h-64 w-64"
          />
        </div>
      )}

      {/* Show message when no category is selected */}
      {/* {!selectedCategory && !loading && (
        <div className="text-center py-12">
          <Lottie animationData={require('../../../assets/loading-product.json')} loop={true} className="h-64 w-64" />
        </div>
      )} */}

      {/* Cart Notification */}
      {showCartNotification && (
        <div className="fixed inset-0 flex items-center justify-center z-50 transition-all duration-300 ease-in-out animate-notification">
          <div className="bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg flex flex-col items-center space-y-4">
            {typeof Lottie !== "undefined" && cartAnimation && (
              <Lottie
                animationData={cartAnimation}
                loop={false}
                className="h-24 w-24"
              />
            )}
            <p className="text-xl">Item added to cart successfully!</p>
          </div>
        </div>
      )}

      {/* Mobile Loading Indicator */}
      {showFullPageLoading && (
        <div className="fixed inset-0 bg-white bg-opacity-70 backdrop-blur-sm flex justify-center items-center z-50">
          {showLoadingAnimation && loadingAnimationUrl && (
            <img src={loadingAnimationUrl} alt="Loading..." />
          )}
        </div>
      )}

      {/* Section Header */}
      <div className="flex justify-between items-center mb-12">
        <div>
          {title && (
            <h2 className="text-3xl font-serif font-bold text-gray-900">
              {title}
            </h2>
          )}
          {description && (
            <p className="text-lg text-gray-600 mt-2">{description}</p>
          )}
        </div>
        {/* {searchEnabled && (
          <div className="relative w-64">
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={categorySearchTerm}
              onChange={(e) => setCategorySearchTerm(e.target.value)}
              className="w-full px-4 py-2 pl-10 rounded-full border-2 border-gray-300 focus:outline-none focus:border-blue-500"
            />
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={16}
            />
          </div>
        )} */}
      </div>

      {filteredCategories.length < sliderThreshold ? (
        <>
          {/* Desktop/Tablet Vertical Layout */}
          <div
            className="hidden md:flex flex-col md:flex-row gap-6"
            style={{ overflowX: "hidden" }}
          >
            {/* Left Column: Category or Parent Category List with Images */}
            <div className="w-full md:w-1/3">
              {content.showParentCategories
                ? // Parent Categories Mode
                parentCategories
                  .filter(
                    (parent) =>
                      showAllParentCategories ||
                      (selectedParentCategoryIds &&
                        selectedParentCategoryIds.includes(parent._id))
                  )
                  .map((parent) => (
                    <div key={parent._id} className="mb-6">
                      {/* Modified parent category rendering */}
                      <div
                        onClick={() =>
                          setSelectedCategory(parent._id + "_parent")
                        }
                        className={`flex items-center p-4 border-b border-gray-200 cursor-pointer category-item`}
                        style={{
                          backgroundColor:
                            selectedCategory === parent._id + "_parent"
                              ? selectedCategoryBackgroundColor || "#60a5fa"
                              : categoryBackgroundColor || "#f3f4f6",
                          color:
                            selectedCategory === parent._id + "_parent"
                              ? selectedCategoryTextColor || "#ffffff"
                              : categoryTextColor || "#1f2937",
                        }}
                      >
                        {parentCategoryDisplayStyle !== "nameOnly" && (
                          <div
                            className={`${verticalImageSizes[verticalImageSize]
                              } ${categoryImageShape === "circle"
                                ? "rounded-full"
                                : "rounded-lg"
                              } overflow-hidden border-4 mr-4`}
                            style={{
                              borderColor:
                                selectedCategory === parent._id + "_parent"
                                  ? selectedCategoryBorderColor
                                  : categoryBorderColor,
                            }}
                          >
                            {/* <LazyImage
                                src={parent.image?.data}
                                alt={parent.name}
                                className="w-full h-full object-cover"
                              /> */}
                            <img
                              src={
                                parent.image?.thumbnail
                                  ? `/api${parent.image.thumbnail}`
                                  : parent.image?.path
                                    ? `/api${parent.image.path}`
                                    : parent.image?.data ||
                                    "/placeholder-image.jpg"
                              }
                              alt={parent.name}
                              className="w-full h-full object-cover"
                              loading="eager"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "/placeholder-image.jpg";
                              }}
                            />
                          </div>
                        )}
                        <p className="font-medium text-lg">{parent.name}</p>
                      </div>

                      {/* Child Categories Row */}
                      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 pl-4 py-2">
                        {getCategoriesByParent(parent._id).map((category) => (
                          <div
                            key={category._id}
                            onClick={() => setSelectedCategory(category._id)}
                            className={`flex-none mr-3 cursor-pointer`}
                          >
                            <div
                              className={`${content.childCategoryImageSize === "tiny"
                                ? "w-12 h-12 min-h-12 max-h-12"
                                : content.childCategoryImageSize === "small"
                                  ? "w-18 h-18 min-h-18 max-h-18"
                                  : "w-24 h-24 min-h-24 max-h-24"
                                } ${categoryImageShape === "circle"
                                  ? "rounded-full"
                                  : "rounded-lg"
                                } overflow-hidden border-2 mb-1`}
                              style={{
                                borderColor:
                                  selectedCategory === category._id
                                    ? selectedCategoryBorderColor
                                    : categoryBorderColor,
                              }}
                            >
                              {/* <LazyImage
                                  src={category.image?.data}
                                  alt={category.name}
                                  className="w-full h-full object-cover"
                                /> */}
                              <img
                                src={
                                  category.image?.thumbnail
                                    ? `/api${category.image.thumbnail}`
                                    : category.image?.path
                                      ? `/api${category.image.path}`
                                      : category.image?.data ||
                                      "/placeholder-image.jpg"
                                }
                                alt={category.name}
                                className="w-full h-full object-cover"
                                loading="eager"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "/placeholder-image.jpg";
                                }}
                              />
                            </div>
                            <p
                              className="text-xs font-medium text-center"
                              style={{
                                maxWidth:
                                  content.childCategoryImageSize === "tiny"
                                    ? "50px"
                                    : content.childCategoryImageSize ===
                                      "small"
                                      ? "74px"
                                      : "96px",
                              }}
                            >
                              {category.name}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                : // Regular Categories Mode
                filteredCategories.map((category) => (
                  <div
                    key={category._id}
                    onClick={() => setSelectedCategory(category._id)}
                    className={`flex items-center p-4 border-b border-gray-200 cursor-pointer category-item`}
                    style={{
                      backgroundColor:
                        selectedCategory === category._id
                          ? selectedCategoryBackgroundColor || "#60a5fa"
                          : categoryBackgroundColor || "#f3f4f6",
                      color:
                        selectedCategory === category._id
                          ? selectedCategoryTextColor || "#ffffff"
                          : categoryTextColor || "#1f2937",
                    }}
                  >
                    <div
                      className={`${verticalImageSizes[verticalImageSize]} ${categoryImageShape === "circle"
                        ? "rounded-full"
                        : "rounded-lg"
                        } overflow-hidden border-4 mr-4`}
                      style={{
                        borderColor:
                          selectedCategory === category._id
                            ? selectedCategoryBorderColor
                            : categoryBorderColor,
                      }}
                    >
                      {/* <LazyImage
                          src={category.image?.data}
                          alt={category.name}
                          className="w-full h-full object-cover"
                        /> */}
                      <img
                        src={
                          category.image?.thumbnail
                            ? `/api${category.image.thumbnail}`
                            : category.image?.path
                              ? `/api${category.image.path}`
                              : category.image?.data || "/placeholder-image.jpg"
                        }
                        alt={category.name}
                        className="w-full h-full object-cover"
                        loading="eager"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/placeholder-image.jpg";
                        }}
                      />
                    </div>
                    <p className="font-medium text-lg">{category.name}</p>
                  </div>
                ))}
            </div>

            {/* Right Column: Related Products for the Selected Category */}
            <div className="w-full md:w-2/3 pb-6">
              {selectedCategory ? (
                <>
                  <div
                    key={selectedCategory}
                    className={`grid ${getGridColumns()} gap-6 ${fadeEffectEnabled ? "fade-in" : ""
                      } pb-4`}
                  >
                    {products
                      .filter((product) => {
                        // Handle parent and regular category selection
                        if (selectedCategory.includes("_parent")) {
                          // For parent categories, show products from all child categories
                          const parentId = selectedCategory.replace(
                            "_parent",
                            ""
                          );
                          const childCategoryIds = getCategoriesByParent(
                            parentId
                          ).map((c) => c._id);
                          return childCategoryIds.some(
                            (childId) =>
                              product.category?._id === childId ||
                              product.category?.__id === childId
                          );
                        } else {
                          // Regular category filtering
                          return (
                            product.category?.__id === selectedCategory ||
                            product.category?._id === selectedCategory
                          );
                        }
                      })
                      .slice(0, visibleCount)
                      .map((product) => (
                        <div
                          key={product._id}
                          className={`bg-white rounded-lg ${cardStyles[productCardStyle]} overflow-hidden transition-transform hover:scale-105 ${productInfoStyles[productInfoPosition]}`}
                        >
                          {/* Product image */}
                          <div
                            className={`relative ${productInfoPosition === "below" ||
                              productInfoPosition === "overlay"
                              ? "w-full"
                              : productInfoPosition === "right"
                                ? "w-1/2"
                                : "order-2 w-1/2"
                              } cursor-pointer ${productImageRatios[productImageAspectRatio]
                              }`}
                            onClick={() => handleProductClick(product)}
                          >
                            {/* <LazyImage
                              src={product.images?.[0]?.data}
                              alt={product.name}
                              className="absolute inset-0 w-full h-full object-cover"
                            /> */}
                            <img
                              src={
                                product.images && product.images.length > 0
                                  ? product.images[0].thumbnail
                                    ? `/api${product.images[0].thumbnail}`
                                    : product.images[0].path
                                      ? `/api${product.images[0].path}`
                                      : product.images[0].data ||
                                      "/placeholder-image.jpg"
                                  : "/placeholder-image.jpg"
                              }
                              alt={product.name}
                              className="w-full h-full object-cover"
                              loading="eager"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "/placeholder-image.jpg";
                              }}
                            />
                            {/* Overlay product info if that position is selected */}
                            {productInfoPosition === "overlay" && (
                              <div className="absolute inset-0 bg-black bg-opacity-40 flex flex-col justify-end p-4">
                                {productDisplayFields.showName !== false && (
                                  <h3
                                    className={`font-bold mb-1 text-${productTextAlignment} text-white`}
                                    style={{
                                      WebkitLineClamp:
                                        productNameLines > 0
                                          ? productNameLines
                                          : "none",
                                      display:
                                        productNameLines > 0
                                          ? "-webkit-box"
                                          : "block",
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {product.name}
                                  </h3>
                                )}

                                {/* {productDisplayFields.showPrice !== false && (
                                  <div className={`flex flex-col ${productTextAlignment === 'center' ? 'items-center' : productTextAlignment === 'right' ? 'items-end' : 'items-start'}`}>
                                    <span className="text-lg font-bold text-green-600">
                                      {priceDisplayType === 'tier' && product.pricingTiers && product.pricingTiers.length > 0 ?
                                        `$${product.pricingTiers[product.pricingTiers.length - 1].price.toFixed(2)}` :
                                      priceDisplayType === 'wholesale' && product.basePrice ?
                                        `$${product.basePrice.toFixed(2)}` :
                                        `$${product.regularPrice ? product.regularPrice.toFixed(2) : product.basePrice.toFixed(2)}`}
                                    </span>
                                    
                                    {priceDisplayType === 'tier' && product.pricingTiers && product.pricingTiers.length > 0 && (
                                      <span className="text-xs text-blue-600">
                                        Tier price
                                      </span>
                                    )}
                                    {priceDisplayType === 'wholesale' && (
                                      <span className="text-xs text-blue-600">Wholesale price</span>
                                    )}
                                    {priceDisplayType === 'regular' && (
                                      <span className="text-xs text-blue-600">Regular price</span>
                                    )}
                                  </div>
                                )} */}
                                {productDisplayFields.showPrice !== false &&
                                  (canSeePrice() ? (
                                    <div
                                      className={`flex flex-col ${productTextAlignment === "center"
                                        ? "items-center"
                                        : productTextAlignment === "right"
                                          ? "items-end"
                                          : "items-start"
                                        }`}
                                    >
                                      <span className="text-lg font-bold text-green-600">
                                        {priceDisplayType === "tier" &&
                                          product.pricingTiers &&
                                          product.pricingTiers.length > 0
                                          ? `$${product.pricingTiers[
                                            product.pricingTiers.length - 1
                                          ].price.toFixed(2)}`
                                          : priceDisplayType === "wholesale" &&
                                            product.basePrice
                                            ? `$${product.basePrice.toFixed(2)}`
                                            : `$${product.regularPrice
                                              ? product.regularPrice.toFixed(
                                                2
                                              )
                                              : product.basePrice.toFixed(2)
                                            }`}
                                      </span>

                                      {priceDisplayType === "tier" &&
                                        product.pricingTiers &&
                                        product.pricingTiers.length > 0 && (
                                          <span className="text-xs text-blue-600">
                                            Tier price
                                          </span>
                                        )}
                                      {priceDisplayType === "wholesale" && (
                                        <span className="text-xs text-blue-600">
                                          Wholesale price
                                        </span>
                                      )}
                                      {priceDisplayType === "regular" && (
                                        <span className="text-xs text-blue-600">
                                          Regular price
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="text-sm italic text-gray-600">
                                      {loginPromptMessage}
                                    </div>
                                  ))}

                                {productDisplayFields.showDescription &&
                                  product.description && (
                                    <p
                                      className={`text-sm text-gray-200 text-${productTextAlignment}`}
                                      style={{
                                        WebkitLineClamp:
                                          productDescriptionLines > 0
                                            ? productDescriptionLines
                                            : "none",
                                        display:
                                          productDescriptionLines > 0
                                            ? "-webkit-box"
                                            : "block",
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                      }}
                                    >
                                      {product.description}
                                    </p>
                                  )}
                              </div>
                            )}
                          </div>

                          {/* Product info not in overlay position */}
                          {productInfoPosition !== "overlay" && (
                            <div
                              className={`p-4 ${productInfoPosition === "below"
                                ? "w-full"
                                : productInfoPosition === "right"
                                  ? "flex-1"
                                  : productInfoPosition === "left"
                                    ? "order-1 flex-1"
                                    : ""
                                } cursor-pointer`}
                              onClick={() => handleProductClick(product)}
                            >
                              {productDisplayFields.showName !== false && (
                                <h3
                                  className={`font-bold mb-2 text-gray-900 text-${productTextAlignment}`}
                                  style={{
                                    WebkitLineClamp:
                                      productNameLines > 0
                                        ? productNameLines
                                        : "none",
                                    display:
                                      productNameLines > 0
                                        ? "-webkit-box"
                                        : "block",
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                >
                                  {product.name}
                                </h3>
                              )}

                              {productDisplayFields.showDescription &&
                                product.description && (
                                  <p
                                    className={`text-sm text-gray-600 mb-2 text-${productTextAlignment}`}
                                    style={{
                                      WebkitLineClamp:
                                        productDescriptionLines > 0
                                          ? productDescriptionLines
                                          : "none",
                                      display:
                                        productDescriptionLines > 0
                                          ? "-webkit-box"
                                          : "block",
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {product.description}
                                  </p>
                                )}

                              {productDisplayFields.showNote &&
                                product.note && (
                                  <p
                                    className={`text-sm italic text-gray-500 mb-2 text-${productTextAlignment}`}
                                  >
                                    {product.note}
                                  </p>
                                )}

                              <div
                                className={`flex justify-between items-center ${productTextAlignment === "center"
                                  ? "justify-center"
                                  : productTextAlignment === "right"
                                    ? "justify-end"
                                    : ""
                                  }`}
                              >
                                {/* {productDisplayFields.showPrice !== false && (
                                  <span className="text-lg font-bold text-blue-600">
                                    ${product.pricingTiers && product.pricingTiers.length > 0 ?
                                      product.pricingTiers[product.pricingTiers.length - 1].price.toFixed(2) :
                                      product.basePrice.toFixed(2)}
                                  </span>
                                )} */}
                                {productDisplayFields.showPrice !== false &&
                                  (canSeePrice() ? (
                                    <div
                                      className={`flex flex-col ${productTextAlignment === "center"
                                        ? "items-center"
                                        : productTextAlignment === "right"
                                          ? "items-end"
                                          : "items-start"
                                        }`}
                                    >
                                      <span className="text-lg font-bold text-green-600">
                                        {priceDisplayType === "tier" &&
                                          product.pricingTiers &&
                                          product.pricingTiers.length > 0
                                          ? `$${product.pricingTiers[
                                            product.pricingTiers.length - 1
                                          ].price.toFixed(2)}`
                                          : priceDisplayType === "wholesale" &&
                                            product.basePrice
                                            ? `$${product.basePrice.toFixed(2)}`
                                            : `$${product.regularPrice
                                              ? product.regularPrice.toFixed(
                                                2
                                              )
                                              : product.basePrice.toFixed(2)
                                            }`}
                                      </span>

                                      {priceDisplayType === "tier" &&
                                        product.pricingTiers &&
                                        product.pricingTiers.length > 0 && (
                                          <span className="text-xs text-blue-600">
                                            Tier price
                                            {/* ({product.pricingTiers[product.pricingTiers.length - 1].minQuantity}+) */}
                                          </span>
                                        )}
                                      {priceDisplayType === "wholesale" && (
                                        <span className="text-xs text-blue-600">
                                          Wholesale price
                                        </span>
                                      )}
                                      {priceDisplayType === "regular" && (
                                        <span className="text-xs text-blue-600">
                                          Regular price
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="text-sm italic text-gray-600">
                                      {/* {loginPromptMessage} */}
                                    </div>
                                  ))}

                                {showQuickAction && (
                                  <div className="flex gap-2">
                                    <button
                                      style={{
                                        boxShadow: "none",
                                        border: "none",
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleProductClick(product);
                                      }}
                                      className="text-gray-600 hover:text-blue-600 transition-colors"
                                      title={
                                        quickActionIcon === "customize"
                                          ? "Customize"
                                          : "Add to Cart"
                                      }
                                    >
                                      {quickActionIcon === "customize" ? (
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          className="h-6 w-6"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                          />
                                        </svg>
                                      ) : (
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          className="h-6 w-6"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                                          />
                                        </svg>
                                      )}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    {selectedCategory.includes("_parent")
                      ? // For parent category, check if any child category has products
                      getCategoriesByParent(
                        selectedCategory.replace("_parent", "")
                      ).length > 0 &&
                      !products.some((product) => {
                        const parentId = selectedCategory.replace(
                          "_parent",
                          ""
                        );
                        const childCategoryIds = getCategoriesByParent(
                          parentId
                        ).map((c) => c._id);
                        return childCategoryIds.some(
                          (childId) =>
                            product.category?._id === childId ||
                            product.category?.__id === childId
                        );
                      }) && (
                        <p className="text-gray-600 col-span-full text-center py-8">
                          No products found for this category.
                        </p>
                      )
                      : // For regular category, show message if no products found
                      !products.some(
                        (product) =>
                          product.category?.__id === selectedCategory ||
                          product.category?._id === selectedCategory
                      ) && (
                        <p className="text-gray-600 col-span-full text-center py-8">
                          No products found for this category.
                        </p>
                      )}
                  </div>

                  {/* Load more button logic - adjust for parent categories */}
                  {selectedCategory.includes("_parent")
                    ? products.filter((product) => {
                      const parentId = selectedCategory.replace(
                        "_parent",
                        ""
                      );
                      const childCategoryIds = getCategoriesByParent(
                        parentId
                      ).map((c) => c._id);
                      return childCategoryIds.some(
                        (childId) =>
                          product.category?._id === childId ||
                          product.category?.__id === childId
                      );
                    }).length > visibleCount && (
                      <div className="flex justify-center mt-8">
                        <button
                          onClick={() => {
                            setIsLoadingMore(true);
                            setTimeout(() => {
                              setVisibleCount(
                                (prev) => prev + loadIncrementDesktop
                              );
                              setIsLoadingMore(false);
                            }, 500);
                          }}
                          className="text-white px-7 py-3 rounded hover:opacity-90 transition-colors"
                          style={{ backgroundColor: loadMoreButtonColor }}
                        >
                          {isLoadingMore ? (
                            showLoadingAnimation ? (
                              <svg
                                className="animate-spin h-5 w-5 text-white mx-auto"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v8H4z"
                                ></path>
                              </svg>
                            ) : (
                              <Lottie
                                animationData={require("../../../assets/loading-product.json")}
                                loop={true}
                                className="h-64 w-64"
                              />
                            )
                          ) : (
                            loadMoreButtonText
                          )}
                        </button>
                      </div>
                    )
                    : // Regular category load more button (unchanged)
                    products.filter(
                      (product) =>
                        product.category?.__id === selectedCategory ||
                        product.category?._id === selectedCategory
                    ).length > visibleCount && (
                      <div className="flex justify-center mt-8">
                        <button
                          onClick={() => {
                            setIsLoadingMore(true);
                            setTimeout(() => {
                              setVisibleCount(
                                (prev) => prev + loadIncrementDesktop
                              );
                              setIsLoadingMore(false);
                            }, 500);
                          }}
                          className="text-white px-7 py-3 rounded hover:opacity-90 transition-colors"
                          style={{ backgroundColor: loadMoreButtonColor }}
                        >
                          {isLoadingMore ? (
                            showLoadingAnimation ? (
                              <svg
                                className="animate-spin h-5 w-5 text-white mx-auto"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8v8H4z"
                                ></path>
                              </svg>
                            ) : (
                              <Lottie
                                animationData={require("../../../assets/loading-product.json")}
                                loop={true}
                                className="h-64 w-64"
                              />
                            )
                          ) : (
                            loadMoreButtonText
                          )}
                        </button>
                      </div>
                    )}
                </>
              ) : (
                <p className="text-gray-600">{/* No Categories Found */}</p>
              )}
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden space-y-6 px-0">
            {/* If showParentCategories is true and we have parent categories, display them with their children */}
            {content.showParentCategories &&
              parentCategories
                .filter(
                  (parent) =>
                    showAllParentCategories ||
                    (selectedParentCategoryIds &&
                      selectedParentCategoryIds.includes(parent._id))
                )
                .map((parent) => (
                  <div key={parent._id + "-mobile"} className="mb-8">
                    {/* Parent Category Heading */}
                    <div
                      className="font-bold text-xl mb-3 px-4 py-2 bg-gray-100 rounded-lg"
                      style={{
                        backgroundColor: categoryBackgroundColor || "#f3f4f6",
                        color: categoryTextColor || "#1f2937",
                      }}
                    >
                      {parent.name}
                    </div>

                    {/* Child Categories for this parent */}
                    {getCategoriesByParent(parent._id).map((category) => (
                      <div key={category._id}>
                        <div
                          style={{
                            marginLeft: -17 + "px",
                            marginRight: -16 + "px",
                            overflow: "hidden",
                            overflowX: "hidden",
                          }}
                        >
                          <div
                            id={`category-${category._id}`}
                            onClick={() => {
                              if (window.innerWidth < 768) {
                                setShowFullPageLoading(true);
                                setTimeout(() => {
                                  setSelectedCategory(category._id);
                                  setShowFullPageLoading(false);
                                  scrollToCategory(category._id);
                                }, 500);
                              } else {
                                setSelectedCategory(category._id);
                              }
                            }}
                            className={`flex items-center p-4 border border-gray-200 cursor-pointer category-item`}
                            style={{
                              backgroundColor:
                                selectedCategory === category._id
                                  ? selectedCategoryBackgroundColor || "#60a5fa"
                                  : categoryBackgroundColor || "#f3f4f6",
                              color:
                                selectedCategory === category._id
                                  ? selectedCategoryTextColor || "#ffffff"
                                  : categoryTextColor || "#1f2937",
                            }}
                          >
                            <div
                              className={`${verticalImageSizes.small} ${categoryImageShape === "circle"
                                ? "rounded-full"
                                : "rounded-lg"
                                } overflow-hidden border-4 mr-4`}
                              style={{
                                borderColor:
                                  selectedCategory === category._id
                                    ? selectedCategoryBorderColor
                                    : categoryBorderColor,
                              }}
                            >
                              {/* <LazyImage
                                src={category.image?.data}
                                alt={category.name}
                                className="w-full h-full object-cover"
                              /> */}
                              <img
                                src={
                                  category.image?.thumbnail
                                    ? `/api${category.image.thumbnail}`
                                    : category.image?.path
                                      ? `/api${category.image.path}`
                                      : category.image?.data ||
                                      "/placeholder-image.jpg"
                                }
                                alt={category.name}
                                className="w-full h-full object-cover"
                                loading="eager"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "/placeholder-image.jpg";
                                }}
                              />
                            </div>
                            <p className="font-medium text-lg">
                              {category.name}
                            </p>
                          </div>
                        </div>
                        {selectedCategory === category._id &&
                          (isLoadingMore ? (
                            <div className="flex justify-center items-center py-2">
                              {showLoadingAnimation ? (
                                <svg
                                  className="animate-spin h-6 w-6 text-blue-600"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8v8H4z"
                                  ></path>
                                </svg>
                              ) : (
                                <p>Loading...</p>
                              )}
                            </div>
                          ) : (
                            <div
                              id={`category-products-${category._id}`}
                              className="mt-4 mb-4"
                            >
                              <div className={`grid ${getGridColumns()} gap-6`}>
                                {products
                                  .filter((product) =>
                                    product.category?.__id
                                      ? product.category.__id === category._id
                                      : product.category?._id === category._id
                                  )
                                  .slice(0, visibleCount)
                                  .map((product) => (
                                    <div
                                      key={product._id}
                                      className={`bg-white rounded-lg ${cardStyles[productCardStyle]} overflow-hidden transition-transform hover:scale-105 ${productInfoStyles[productInfoPosition]}`}
                                    >
                                      <div
                                        className={`relative ${productInfoPosition === "below" ||
                                          productInfoPosition === "overlay"
                                          ? "w-full"
                                          : "w-1/2 min-w-1/2"
                                          } ${productImageRatios[
                                          productImageAspectRatio
                                          ]
                                          } cursor-pointer`}
                                        onClick={() =>
                                          handleProductClick(product)
                                        }
                                      >
                                        {/* <LazyImage
                                          src={product.images?.[0]?.data}
                                          alt={product.name}
                                          className="absolute inset-0 w-full h-full object-cover"
                                        /> */}
                                        <img
                                          src={
                                            product.images &&
                                              product.images.length > 0
                                              ? product.images[0].thumbnail
                                                ? `/api${product.images[0].thumbnail}`
                                                : product.images[0].path
                                                  ? `/api${product.images[0].path}`
                                                  : product.images[0].data ||
                                                  "/placeholder-image.jpg"
                                              : "/placeholder-image.jpg"
                                          }
                                          alt={product.name}
                                          className="w-full h-full object-cover"
                                          loading="eager"
                                          onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src =
                                              "/placeholder-image.jpg";
                                          }}
                                        />
                                      </div>
                                      <div
                                        className={`p-4 cursor-pointer ${productInfoPosition === "below" ||
                                          productInfoPosition === "overlay"
                                          ? "w-full"
                                          : "w-1/2 max-w-1/2"
                                          } 
                                         `}
                                        onClick={() =>
                                          handleProductClick(product)
                                        }
                                      >
                                        {productDisplayFields.showName !==
                                          false && (
                                            <h3
                                              className={`text-md font-bold text-gray-900 mb-2 text-${productTextAlignment}`}
                                            >
                                              {product.name}
                                            </h3>
                                          )}
                                        <div
                                          className={`flex justify-between items-center ${productTextAlignment === "center"
                                            ? "justify-center"
                                            : productTextAlignment === "right"
                                              ? "justify-end"
                                              : ""
                                            }`}
                                        >
                                          {productDisplayFields.showPrice !== false &&
                                            canSeePrice() && (
                                              <div
                                                className={`flex flex-col ${productTextAlignment ===
                                                  "center"
                                                  ? "items-center"
                                                  : productTextAlignment ===
                                                    "right"
                                                    ? "items-end"
                                                    : "items-start"
                                                  }`}
                                              >
                                                <span className="text-lg font-bold text-green-600">
                                                  {priceDisplayType === "tier" &&
                                                    product.pricingTiers &&
                                                    product.pricingTiers.length > 0
                                                    ? `$${product.pricingTiers[
                                                      product.pricingTiers
                                                        .length - 1
                                                    ].price.toFixed(2)}`
                                                    : priceDisplayType ===
                                                      "wholesale" &&
                                                      product.basePrice
                                                      ? `$${product.basePrice.toFixed(
                                                        2
                                                      )}`
                                                      : `$${product.regularPrice
                                                        ? product.regularPrice.toFixed(
                                                          2
                                                        )
                                                        : product.basePrice.toFixed(
                                                          2
                                                        )
                                                      }`}
                                                </span>

                                                {priceDisplayType === "tier" &&
                                                  product.pricingTiers &&
                                                  product.pricingTiers.length >
                                                  0 && (
                                                    <span className="text-xs text-blue-600">
                                                      Tier price
                                                    </span>
                                                  )}
                                                {priceDisplayType ===
                                                  "wholesale" && (
                                                    <span className="text-xs text-blue-600">
                                                      Wholesale price
                                                    </span>
                                                  )}
                                                {priceDisplayType ===
                                                  "regular" && (
                                                    <span className="text-xs text-blue-600">
                                                      Regular price
                                                    </span>
                                                  )}
                                              </div>
                                            )}
                                          {showQuickAction && (
                                            <button
                                              style={{
                                                boxShadow: "none",
                                                border: "none",
                                              }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleProductClick(product);
                                              }}
                                              className="text-gray-600 hover:text-blue-600 transition-colors"
                                              title={
                                                quickActionIcon === "customize"
                                                  ? "Customize"
                                                  : "Add to Cart"
                                              }
                                            >
                                              {quickActionIcon ===
                                                "customize" ? (
                                                <svg
                                                  xmlns="http://www.w3.org/2000/svg"
                                                  className="h-6 w-6"
                                                  fill="none"
                                                  viewBox="0 0 24 24"
                                                  stroke="currentColor"
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                                  />
                                                </svg>
                                              ) : (
                                                <svg
                                                  xmlns="http://www.w3.org/2000/svg"
                                                  className="h-6 w-6"
                                                  fill="none"
                                                  viewBox="0 0 24 24"
                                                  stroke="currentColor"
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                                                  />
                                                </svg>
                                              )}
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                {products.filter((product) =>
                                  product.category?.__id
                                    ? product.category.__id === category._id
                                    : product.category?._id === category._id
                                ).length === 0 && (
                                    <p className="text-gray-600 col-span-full text-center py-4">
                                      No products found for this category.
                                    </p>
                                  )}
                              </div>
                              {products.filter((product) =>
                                product.category?.__id
                                  ? product.category.__id === category._id
                                  : product.category?._id === category._id
                              ).length > visibleCount && (
                                  <div className="flex justify-center mt-8">
                                    <button
                                      onClick={() => {
                                        setIsLoadingMore(true);
                                        setTimeout(() => {
                                          setVisibleCount(
                                            visibleCount + loadIncrementMobile
                                          );
                                          setIsLoadingMore(false);
                                        }, 500);
                                      }}
                                      className="text-white px-7 py-3 rounded hover:opacity-90 transition-colors"
                                      style={{
                                        backgroundColor: loadMoreButtonColor,
                                      }}
                                    >
                                      {isLoadingMore ? (
                                        showLoadingAnimation ? (
                                          <svg
                                            className="animate-spin h-5 w-5 text-white mx-auto"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                          >
                                            <circle
                                              className="opacity-25"
                                              cx="12"
                                              cy="12"
                                              r="10"
                                              stroke="currentColor"
                                              strokeWidth="4"
                                            ></circle>
                                            <path
                                              className="opacity-75"
                                              fill="currentColor"
                                              d="M4 12a8 8 0 018-8v8H4z"
                                            ></path>
                                          </svg>
                                        ) : (
                                          "Loading..."
                                        )
                                      ) : (
                                        loadMoreButtonText
                                      )}
                                    </button>
                                  </div>
                                )}
                            </div>
                          ))}
                      </div>
                    ))}
                  </div>
                ))}

            {/* Regular categories (keep existing rendering) */}
            {(!content.showParentCategories ||
              filteredCategories.filter((category) => !category.parentCategory)
                .length > 0) &&
              filteredCategories
                .filter(
                  (category) =>
                    !content.showParentCategories || !category.parentCategory
                )
                .map((category) => (
                  <div key={category._id}>
                    <div
                      style={{
                        marginLeft: -17 + "px",
                        marginRight: -16 + "px",
                        overflow: "hidden",
                        overflowX: "hidden",
                      }}
                    >
                      <div
                        id={`category-${category._id}`}
                        onClick={() => {
                          if (window.innerWidth < 768) {
                            setShowFullPageLoading(true);
                            setTimeout(() => {
                              setSelectedCategory(category._id);
                              setShowFullPageLoading(false);
                              scrollToCategory(category._id);
                            }, 500);
                          } else {
                            setSelectedCategory(category._id);
                          }
                        }}
                        className={`flex items-center p-4 border border-gray-200 cursor-pointer category-item`}
                        style={{
                          backgroundColor:
                            selectedCategory === category._id
                              ? selectedCategoryBackgroundColor || "#60a5fa"
                              : categoryBackgroundColor || "#f3f4f6",
                          color:
                            selectedCategory === category._id
                              ? selectedCategoryTextColor || "#ffffff"
                              : categoryTextColor || "#1f2937",
                        }}
                      >
                        <div
                          className={`${verticalImageSizes.small} ${categoryImageShape === "circle"
                            ? "rounded-full"
                            : "rounded-lg"
                            } overflow-hidden border-4 mr-4`}
                          style={{
                            borderColor:
                              selectedCategory === category._id
                                ? selectedCategoryBorderColor
                                : categoryBorderColor,
                          }}
                        >
                          {/* <LazyImage
                            src={category.image?.data}
                            alt={category.name}
                            className="w-full h-full object-cover"
                          /> */}
                          <img
                            src={
                              category.image?.thumbnail
                                ? `/api${category.image.thumbnail}`
                                : category.image?.path
                                  ? `/api${category.image.path}`
                                  : category.image?.data ||
                                  "/placeholder-image.jpg"
                            }
                            alt={category.name}
                            className="w-full h-full object-cover"
                            loading="eager"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/placeholder-image.jpg";
                            }}
                          />
                        </div>
                        <p className="font-medium text-lg">{category.name}</p>
                      </div>
                    </div>
                    {selectedCategory === category._id &&
                      (isLoadingMore ? (
                        <div className="flex justify-center items-center py-2">
                          {showLoadingAnimation ? (
                            <svg
                              className="animate-spin h-6 w-6 text-blue-600"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v8H4z"
                              ></path>
                            </svg>
                          ) : (
                            <p>Loading...</p>
                          )}
                        </div>
                      ) : (
                        <div
                          id={`category-products-${category._id}`}
                          className="mt-4"
                        >
                          <div className={`grid ${getGridColumns()} gap-6`}>
                            {products
                              .filter((product) =>
                                product.category?.__id
                                  ? product.category.__id === category._id
                                  : product.category?._id === category._id
                              )
                              .slice(0, visibleCount)
                              .map((product) => (
                                <div
                                  key={product._id}
                                  className={`bg-white rounded-lg ${cardStyles[productCardStyle]} overflow-hidden transition-transform hover:scale-105 ${productInfoStyles[productInfoPosition]}`}
                                >
                                  <div
                                    className={`relative ${productInfoPosition === "below"
                                      ? "w-full"
                                      : "w-1/2"
                                      } ${productImageRatios[
                                      productImageAspectRatio
                                      ]
                                      } cursor-pointer`}
                                    onClick={() => handleProductClick(product)}
                                  >
                                    {/* <LazyImage
                                      src={product.images?.[0]?.data}
                                      alt={product.name}
                                      className="absolute inset-0 w-full h-full object-cover"
                                    /> */}
                                    <img
                                      src={
                                        product.images &&
                                          product.images.length > 0
                                          ? product.images[0].thumbnail
                                            ? `/api${product.images[0].thumbnail}`
                                            : product.images[0].path
                                              ? `/api${product.images[0].path}`
                                              : product.images[0].data ||
                                              "/placeholder-image.jpg"
                                          : "/placeholder-image.jpg"
                                      }
                                      alt={product.name}
                                      className="w-full h-full object-cover"
                                      loading="eager"
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "/placeholder-image.jpg";
                                      }}
                                    />
                                  </div>
                                  <div
                                    className="p-4 cursor-pointer"
                                    onClick={() => handleProductClick(product)}
                                  >
                                    {productDisplayFields.showName !==
                                      false && (
                                        <h3
                                          className={`text-md font-bold text-gray-900 mb-2 text-${productTextAlignment}`}
                                        >
                                          {product.name}
                                        </h3>
                                      )}
                                    <div
                                      className={`flex justify-between items-center ${productTextAlignment === "center"
                                        ? "justify-center"
                                        : productTextAlignment === "right"
                                          ? "justify-end"
                                          : ""
                                        }`}
                                    >
                                      {productDisplayFields.showPrice !==
                                        false &&
                                        canSeePrice() && (
                                          <div
                                            className={`flex flex-col ${productTextAlignment === "center"
                                              ? "items-center"
                                              : productTextAlignment ===
                                                "right"
                                                ? "items-end"
                                                : "items-start"
                                              }`}
                                          >
                                            <span className="text-lg font-bold text-green-600">
                                              {priceDisplayType === "tier" &&
                                                product.pricingTiers &&
                                                product.pricingTiers.length > 0
                                                ? `$${product.pricingTiers[
                                                  product.pricingTiers
                                                    .length - 1
                                                ].price.toFixed(2)}`
                                                : priceDisplayType ===
                                                  "wholesale" &&
                                                  product.basePrice
                                                  ? `$${product.basePrice.toFixed(
                                                    2
                                                  )}`
                                                  : `$${product.regularPrice
                                                    ? product.regularPrice.toFixed(
                                                      2
                                                    )
                                                    : product.basePrice.toFixed(
                                                      2
                                                    )
                                                  }`}
                                            </span>

                                            {priceDisplayType === "tier" &&
                                              product.pricingTiers &&
                                              product.pricingTiers.length >
                                              0 && (
                                                <span className="text-xs text-blue-600">
                                                  Tier price
                                                </span>
                                              )}
                                            {priceDisplayType ===
                                              "wholesale" && (
                                                <span className="text-xs text-blue-600">
                                                  Wholesale price
                                                </span>
                                              )}
                                            {priceDisplayType === "regular" && (
                                              <span className="text-xs text-blue-600">
                                                Regular price
                                              </span>
                                            )}
                                          </div>
                                        )}
                                      {showQuickAction && (
                                        <button
                                          style={{
                                            boxShadow: "none",
                                            border: "none",
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleProductClick(product);
                                          }}
                                          className="text-gray-600 hover:text-blue-600 transition-colors"
                                          title={
                                            quickActionIcon === "customize"
                                              ? "Customize"
                                              : "Add to Cart"
                                          }
                                        >
                                          {quickActionIcon === "customize" ? (
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              className="h-6 w-6"
                                              fill="none"
                                              viewBox="0 0 24 24"
                                              stroke="currentColor"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                              />
                                            </svg>
                                          ) : (
                                            <svg
                                              xmlns="http://www.w3.org/2000/svg"
                                              className="h-6 w-6"
                                              fill="none"
                                              viewBox="0 0 24 24"
                                              stroke="currentColor"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                                              />
                                            </svg>
                                          )}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            {products.filter((product) =>
                              product.category?.__id
                                ? product.category.__id === category._id
                                : product.category?._id === category._id
                            ).length === 0 && (
                                <p className="text-gray-600 col-span-full text-center py-4">
                                  No products found for this category.
                                </p>
                              )}
                          </div>
                          {products.filter((product) =>
                            product.category?.__id
                              ? product.category.__id === category._id
                              : product.category?._id === category._id
                          ).length > visibleCount && (
                              <div className="flex justify-center mt-8">
                                <button
                                  onClick={() => {
                                    setIsLoadingMore(true);
                                    setTimeout(() => {
                                      setVisibleCount(
                                        visibleCount + loadIncrementMobile
                                      );
                                      setIsLoadingMore(false);
                                    }, 500);
                                  }}
                                  className="text-white px-7 py-3 rounded hover:opacity-90 transition-colors"
                                  style={{ backgroundColor: loadMoreButtonColor }}
                                >
                                  {isLoadingMore ? (
                                    showLoadingAnimation ? (
                                      <svg
                                        className="animate-spin h-5 w-5 text-white mx-auto"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                      >
                                        <circle
                                          className="opacity-25"
                                          cx="12"
                                          cy="12"
                                          r="10"
                                          stroke="currentColor"
                                          strokeWidth="4"
                                        ></circle>
                                        <path
                                          className="opacity-75"
                                          fill="currentColor"
                                          d="M4 12a8 8 0 018-8v8H4z"
                                        ></path>
                                      </svg>
                                    ) : (
                                      "Loading..."
                                    )
                                  ) : (
                                    loadMoreButtonText
                                  )}
                                </button>
                              </div>
                            )}
                        </div>
                      ))}
                  </div>
                ))}
          </div>
        </>
      ) : (
        // Slider layout for many categories
        <div className="relative">
          <button
            onClick={() => scroll("left")}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100"
          >
            <ChevronLeft size={24} />
          </button>

          <div
            ref={categorySliderRef}
            className="flex overflow-x-auto hide-scrollbar gap-6 px-12"
            style={{ paddingTop: "15px" }}
          >
            {filteredCategories.map((category) => (
              <div
                key={category._id}
                onClick={() => {
                  setSelectedCategory(category._id);
                  // window.history.pushState(
                  //   {},
                  //   "",
                  //   `/cat/${
                  //     category.slug ||
                  //     category.name.toLowerCase().replace(/\s+/g, "-")
                  //   }`
                  // );
                }}
                className="flex-none cursor-pointer transition-transform hover:scale-105"
              >
                <div
                  className={`${sliderImageSizes[sliderImageSize]} ${categoryImageShape === "circle"
                    ? "rounded-full"
                    : "rounded-lg"
                    } overflow-hidden border-4 mb-3`}
                  style={{ borderColor: categoryBorderColor }}
                >
                  {/* <LazyImage
                    src={category.image?.data}
                    alt={category.name}
                    className="w-full h-full object-cover"
                  /> */}
                  <img
                    src={
                      category.image?.thumbnail
                        ? `/api${category.image.thumbnail}`
                        : category.image?.path
                          ? `/api${category.image.path}`
                          : category.image?.data || "/placeholder-image.jpg"
                    }
                    alt={category.name}
                    className="w-full h-full object-cover"
                    loading="eager"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/placeholder-image.jpg";
                    }}
                  />
                </div>
                <p className="text-center font-medium text-gray-800">
                  {category.name}
                </p>
              </div>
            ))}
          </div>

          <button
            onClick={() => scroll("right")}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100"
          >
            <ChevronRight size={24} />
          </button>
        </div>
      )}

      {/* Product Detail Popup */}
      {showProductPopup && selectedProduct && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-75 flex items-center justify-center p-4">
          <div
            className={`bg-white rounded-lg shadow-xl relative max-h-screen overflow-auto ${
              // On mobile, override the size to be smaller regardless of the setting
              windowWidth < 768
                ? "w-full max-w-xs"
                : productPopupSettings.popupSize === "small"
                  ? "w-full max-w-lg"
                  : productPopupSettings.popupSize === "medium"
                    ? "w-full max-w-2xl"
                    : productPopupSettings.popupSize === "large"
                      ? "w-full max-w-4xl"
                      : "w-full max-w-7xl h-full"
              }`}
          >
            {/* Close button */}
            <button
              onClick={() => setShowProductPopup(false)}
              style={{
                // boxShadow: "none"
                backgroundColor: "#444444",
                color: "white",
                borderRadius: "40px",
                padding: 5,
              }}
              className="absolute top-2 right-1 text-gray-500 hover:text-gray-700 z-10"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            <div className="flex flex-col md:flex-row">
              {/* Product images */}
              {productPopupSettings.showGallery !== false && (
                <div className="md:w-1/2 p-4">
                  <div className="bg-gray-100 rounded-lg overflow-hidden">
                    {selectedProduct.images &&
                      selectedProduct.images.length > 0 ? (
                      <div className="relative">
                        {/* <LazyImage
                          src={selectedProduct.images[0]?.data}
                          alt={selectedProduct.name}
                          className={`w-full h-auto object-contain aspect-square`}
                        /> */}
                        <img
                          src={
                            selectedProduct.images[0]?.thumbnail
                              ? `/api${selectedProduct.images[0].thumbnail}`
                              : selectedProduct.images[0]?.path
                                ? `/api${selectedProduct.images[0].path}`
                                : selectedProduct.images[0]?.data ||
                                "/placeholder-image.jpg"
                          }
                          alt={selectedProduct.name}
                          className="w-full h-auto object-contain aspect-square"
                          loading="eager"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/placeholder-image.jpg";
                          }}
                        />
                        {windowWidth < 768 && (
                          <div
                            className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 h-1/5 px-3 pt-2"
                            style={{ pointerEvents: "none" }}
                          >
                            <h2
                              className={` text-white mb-2 ${
                                // On mobile, override the size to be smaller regardless of the setting
                                windowWidth < 768
                                  ? "text-md font-semibold"
                                  : "text-2xl font-bold"
                                }`}
                            >
                              {selectedProduct.name}
                            </h2>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="w-full h-64 flex items-center justify-center bg-gray-200 text-gray-500">
                        No image available
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Product details */}
              <div
                className={`${productPopupSettings.showGallery !== false
                  ? "md:w-1/2"
                  : "w-full"
                  } p-6 pt-0`}
              >
                {windowWidth >= 768 && (
                  <h2 className="text-lg mt-8 font-semibold text-gray-600 mb-2                 ">
                    {selectedProduct.name}
                  </h2>
                )}
                {productPopupSettings.showDescription !== false &&
                  selectedProduct.description && (
                    <div className="mb-4">
                      {/* <h3 className="font-medium text-gray-700 mb-1">
                        Description
                      </h3> */}
                      <p
                        style={{
                          maxHeight: 200 + "px",
                          overflowY: "auto",
                        }}
                        className={`text-gray-600 ${
                          // On mobile, override the size to be smaller regardless of the setting
                          windowWidth < 768
                            ? "text-sm font-normal max-h-20 h-20"
                            : " "
                          }`}
                      >
                        {selectedProduct.description}
                      </p>
                    </div>
                  )}

                {productPopupSettings.showNote !== false &&
                  selectedProduct.note && (
                    <div className="mb-4">
                      <h3 className="font-medium text-gray-700 mb-1">Note</h3>
                      <p className="text-gray-600 italic">
                        {selectedProduct.note}
                      </p>
                    </div>
                  )}

                {productPopupSettings.showAttributes !== false &&
                  selectedProduct.attributes &&
                  selectedProduct.attributes.length > 0 && (
                    <div className="mb-6">
                      <h3 className="font-medium text-gray-700 mb-2">
                        Specifications
                      </h3>
                      <div className="grid grid-cols-2 gap-2">
                        {selectedProduct.attributes.map((attr, index) => (
                          <div key={index} className="flex flex-col">
                            <span className="text-sm text-gray-500">
                              {attr.name}
                            </span>
                            <span className="font-medium">{attr.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {productPopupSettings.showPrice !== false &&
                  (canSeePrice() ? (
                    <div
                      className={`flex flex-col ${productTextAlignment === "center"
                        ? "items-center"
                        : productTextAlignment === "right"
                          ? "items-end"
                          : "items-start"
                        }`}
                    >
                      {/* ${selectedProduct.pricingTiers && selectedProduct.pricingTiers.length > 0 ?
                        selectedProduct.pricingTiers[selectedProduct.pricingTiers.length - 1].price.toFixed(2) :
                        selectedProduct.basePrice.toFixed(2)} */}
                      <span className="text-lg font-bold text-green-600">
                        {priceDisplayType === "tier" &&
                          selectedProduct.pricingTiers &&
                          selectedProduct.pricingTiers.length > 0
                          ? `$${selectedProduct.pricingTiers[
                            selectedProduct.pricingTiers.length - 1
                          ].price.toFixed(2)}`
                          : priceDisplayType === "wholesale" &&
                            selectedProduct.basePrice
                            ? `$${selectedProduct.basePrice.toFixed(2)}`
                            : `$${selectedProduct.regularPrice
                              ? selectedProduct.regularPrice.toFixed(2)
                              : selectedProduct.basePrice.toFixed(2)
                            }`}
                      </span>

                      {/* {priceDisplayType === "tier" &&
                        selectedProduct.pricingTiers &&
                        selectedProduct.pricingTiers.length > 0 && (
                          <div className="text-xs text-blue-600">
                            <div>Tier pricing:</div>
                            {selectedProduct.pricingTiers
                              .sort((a, b) => a.minQuantity - b.minQuantity)
                              .map((tier, idx) => (
                                <div key={idx} className="whitespace-nowrap">
                                  {tier.maxQuantity
                                    ? `${tier.minQuantity}-${
                                        tier.maxQuantity
                                      }: $${tier.price.toFixed(2)}`
                                    : `${
                                        tier.minQuantity
                                      }+: $${tier.price.toFixed(2)}`}
                                </div>
                              ))}
                          </div>
                        )} */}
                      {priceDisplayType === "tier" &&
                        selectedProduct.pricingTiers &&
                        selectedProduct.pricingTiers.length > 0 && (
                          <div className="text-xs text-blue-600">
                            <span>Tier pricing: </span>
                            <span className="inline-flex flex-wrap gap-x-2">
                              {selectedProduct.pricingTiers
                                .sort((a, b) => a.minQuantity - b.minQuantity)
                                .map((tier, idx, array) => (
                                  <span key={idx} className="whitespace-nowrap">
                                    {tier.maxQuantity
                                      ? `${tier.minQuantity}-${tier.maxQuantity
                                      }: $${tier.price.toFixed(2)}`
                                      : `${tier.minQuantity
                                      }+: $${tier.price.toFixed(2)}`}
                                    {idx < array.length - 1 ? "," : ""}
                                  </span>
                                ))}
                            </span>
                          </div>
                        )}
                      {priceDisplayType === "wholesale" && (
                        <span className="text-xs text-blue-600">
                          Wholesale price
                        </span>
                      )}
                      {priceDisplayType === "regular" && (
                        <span className="text-xs text-blue-600">
                          Regular price
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm italic text-gray-600">
                      {loginPromptMessage}
                    </div>
                  ))}

                {/* Action buttons */}
                {/* <div className="flex flex-wrap gap-4 mt-6">
                  {productPopupSettings.showCustomizeButton !== false && (
                    <button
                      onClick={() => navigateToProduct(selectedProduct)}
                      className="px-6 py-2 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition-colors"
                    >
                      Customize
                    </button>
                  )}
                  
                  {productPopupSettings.showAddToCart !== false && (              
                       <div className="mt-2 flex items-center justify-between">
                       <div className="flex items-center border border-gray-200 rounded overflow-hidden">
                         <button
                           onClick={(e) => {
                             e.stopPropagation();
                             const currentQty = productQuantities[selectedProduct._id] || defaultQuantity;
                             if (currentQty > 1) {
                               setProductQuantities({
                                 ...productQuantities,
                                 [selectedProduct._id]: currentQty - 1
                               });
                             }
                           }}
                           className="px-2 py-1 bg-gray-50 text-gray-600 hover:bg-gray-100"
                         >
                           -
                         </button>
                         <input
                           type="number"
                           min="1"
                           value={productQuantities[selectedProduct._id] || defaultQuantity}
                           onChange={(e) => {
                             e.stopPropagation();
                             const newQty = parseInt(e.target.value, 10) || 1;
                             setProductQuantities({
                               ...productQuantities,
                               [selectedProduct._id]: newQty
                             });
                           }}
                           className="w-24 text-center border-x border-gray-200 py-1"
                         />
                         <button
                           onClick={(e) => {
                             e.stopPropagation();
                             const currentQty = productQuantities[selectedProduct._id] || defaultQuantity;
                             setProductQuantities({
                               ...productQuantities,
                               [selectedProduct._id]: currentQty + 1
                             });
                           }}
                           className="px-2 py-1 bg-gray-50 text-gray-600 hover:bg-gray-100"
                         >
                           +
                         </button>
                    <button
                      onClick={() => {
                        handleAddToCart(selectedProduct);
                        setShowProductPopup(false);
                      }}
                      className="px-6 py-2 bg-green-600 text-white font-medium rounded hover:bg-green-700 transition-colors"
                    >
                      Add to Cart
                    </button>
                    </div>
                     </div>
                  )}
                </div> */}
                {/* Action buttons */}
                <div className="flex flex-wrap gap-4 mt-6">
                  {productPopupSettings.showCustomizeButton !== false && (
                    <button
                      onClick={() => navigateToProduct(selectedProduct)}
                      className="px-6 py-2 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition-colors"
                    >
                      Customize
                    </button>
                  )}

                  {productPopupSettings.showAddToCart !== false &&
                    canAddToCart() && (
                      <div className="mt-2 flex items-center justify-between">
                        <div className="flex items-center border border-gray-200 rounded overflow-hidden">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const currentQty =
                                productQuantities[selectedProduct._id] ||
                                defaultQuantity;
                              if (currentQty > 1) {
                                setProductQuantities({
                                  ...productQuantities,
                                  [selectedProduct._id]: currentQty - 1,
                                });
                              }
                            }}
                            className="px-2 py-1 bg-gray-50 text-gray-600 hover:bg-gray-100"
                          >
                            -
                          </button>
                          <input
                            type="number"
                            min="1"
                            value={
                              productQuantities[selectedProduct._id] ||
                              defaultQuantity
                            }
                            onChange={(e) => {
                              e.stopPropagation();
                              const newQty = parseInt(e.target.value, 10) || 1;
                              setProductQuantities({
                                ...productQuantities,
                                [selectedProduct._id]: newQty,
                              });
                            }}
                            className="w-20 pl-0 text-center border-x border-gray-200 py-1"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const currentQty =
                                productQuantities[selectedProduct._id] ||
                                defaultQuantity;
                              setProductQuantities({
                                ...productQuantities,
                                [selectedProduct._id]: currentQty + 1,
                              });
                            }}
                            className="px-2 py-1 bg-gray-50 text-gray-600 hover:bg-gray-100"
                          >
                            +
                          </button>
                          <button
                            onClick={() => {
                              handleAddToCart(selectedProduct);
                              setShowProductPopup(false);
                            }}
                            className="px-1 py-2 bg-green-600 text-white font-medium rounded hover:bg-green-700 transition-colors"
                          >
                            Add to Cart
                          </button>
                        </div>
                      </div>
                    )}
                  {/* {productPopupSettings.showAddToCart !== false && !canAddToCart() && (
                    <div className="mt-2 text-sm italic text-gray-600">{loginPromptMessage}</div>
                  )} */}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Login Prompt Notification */}
      {showLoginPrompt && (
        <div className="fixed inset-0 flex items-center justify-center z-50 transition-all duration-300 ease-in-out animate-notification">
          <div className="bg-blue-500 text-white px-6 py-4 rounded-lg shadow-lg flex flex-col items-center space-y-4">
            <p className="text-xl">{loginPromptMessage}</p>
            <div className="flex gap-4">
              <button
                onClick={() => (window.location.href = "/login")}
                className="px-4 py-2 bg-white text-blue-600 rounded hover:bg-gray-100"
              >
                Login
              </button>
              <button
                onClick={() => setShowLoginPrompt(false)}
                className="px-4 py-2 border border-white text-white rounded hover:bg-blue-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .fade-in {
          animation: fadeIn 0.5s ease-in-out;
        }
        
        @keyframes notification-animation {
          0% { opacity: 0; transform: scale(0.8); }
          10% { opacity: 1; transform: scale(1.1); }
          20% { transform: scale(1); }
          80% { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.8); }
        }
        
        .animate-notification {
          animation: notification-animation 2s ease-in-out forwards;
        }
        .category-item:hover {
          background-color: ${categoryHoverBackgroundColor} !important;
          color: ${categoryHoverTextColor} !important;
        }
        @keyframes login-prompt-animation {
          0% { opacity: 0; transform: scale(0.8); }
          10% { opacity: 1; transform: scale(1.05); }
          20% { transform: scale(1); }
          80% { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.8); }
        }

        .animate-login-prompt {
          animation: login-prompt-animation 3s ease-in-out forwards;
        }
          .product-grid-item {
  display: flex;
  flex-direction: column;
}

.product-image-container {
  position: relative;
  width: 100%;
  padding-bottom: 100%; /* 1:1 Aspect Ratio by default */
}

.product-image-container.portrait {
  padding-bottom: 133.33%; /* 3:4 Aspect Ratio */
}

.product-image-container.landscape {
  padding-bottom: 75%; /* 4:3 Aspect Ratio */
}

.product-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
      `}</style>
    </div>
  );
};

export default ProductCatalogVerticalSection;

// // befroe new img in upload ..
// // src/components/admin/page-sections/ProductCatalogVerticalSection.js
// import React, { useState, useEffect, useRef } from "react";
// import { ChevronLeft, ChevronRight, Search } from "lucide-react";
// import { useCart } from "../../../context/CartContext";
// import Lottie from "lottie-react";
// import cartAnimation from "../../../assets/cartAnimation.json";
// import LazyImage from "../../LazyImage";
// import { useAuth } from "../../../context/AuthContext";

// const ProductCatalogVerticalSection = ({ content }) => {
//   const {
//     title = "Browse Categories",
//     description = "",
//     searchEnabled = true,
//     searchPlaceholder = "Search categories...",
//     layoutType = "vertical", // 'vertical' or 'slider'
//     sliderThreshold = 9, // Switch to slider if more categories than this
//     categoryImageShape = "circle", // 'circle' or 'square'
//     categoryBorderColor = "#3b82f6", // blue-500
//     selectedCategoryBorderColor = "#3b82f6", // blue-400
//     categoryBackgroundColor = "#f3f4f6", // gray-100
//     selectedCategoryBackgroundColor = "#60a5fa", // blue-400
//     categoryTextColor = "#1f2937", // gray-800
//     selectedCategoryTextColor = "#ffffff", // white
//     categoryHoverBackgroundColor = "#e5e7eb", // gray-200
//     categoryHoverTextColor = "#111827", // gray-900
//     loadMoreButtonText = "Load More",
//     loadMoreButtonColor = "#1e3a8a", // blue-900
//     loadIncrementDesktop = 6,
//     loadIncrementMobile = 3,
//     showLoadingAnimation = true,
//     loadingAnimationUrl = "../images/loading.gif",
//     apiPath = "/api/categories/basic",
//     productApiPath = "/api/products/basic",
//     productGridColumnsResponsive = {
//       mobile: 1,
//       tablet: 2,
//       desktop: 3,
//     },
//     verticalImageSize = "medium", // 'small', 'medium', 'large'
//     sliderImageSize = "medium", // 'small', 'medium', 'large'
//     fadeEffectEnabled = true,
//     productCardStyle = "shadow",
//     showQuickAction = true,
//     quickActionIcon = "customize",
//     backgroundColor = "#F9FAFB",
//     // Product display options
//     productDisplayFields = {
//       showName: true,
//       showPrice: true,
//       showDescription: false,
//       showNote: false,
//       showRating: false,
//     },
//     productInfoPosition = "below",
//     productTextAlignment = "left",
//     productNameLines = 2,
//     productDescriptionLines = 3,
//     productImageAspectRatio = "square",
//     productClickBehavior = "navigate",
//     productPopupSettings = {
//       showGallery: true,
//       showDescription: true,
//       showPrice: true,
//       showNote: true,
//       showAttributes: true,
//       showAddToCart: true,
//       showCustomizeButton: true,
//       popupSize: "medium",
//     },

//     //category
//     showAllCategories = true,
//     selectedCategoryIds = [],
//     showAllParentCategories = true,
//     selectedParentCategoryIds = [],
//     parentCategoryDisplayStyle = "nameAndImage",

//     //pricing & add to cart
//     priceDisplayType = "regular",
//     allowQuantitySelection = false,
//     defaultQuantity = 1,
//     priceVisibility = "all",
//     cartVisibility = "all",
//     loginPromptMessage = "Please login to see prices and purchase products",
//   } = content;

//   const [categories, setCategories] = useState([]);
//   const [products, setProducts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [categorySearchTerm, setCategorySearchTerm] = useState("");
//   const [selectedCategory, setSelectedCategory] = useState(null);
//   const [visibleCount, setVisibleCount] = useState(
//     window.innerWidth < 768 ? loadIncrementMobile : loadIncrementDesktop
//   );
//   const [isLoadingMore, setIsLoadingMore] = useState(false);
//   const [showFullPageLoading, setShowFullPageLoading] = useState(false);
//   const categorySliderRef = useRef(null);
//   const [selectedProduct, setSelectedProduct] = useState(null);
//   const [showProductPopup, setShowProductPopup] = useState(false);
//   const [showCartNotification, setShowCartNotification] = useState(false);
//   const cartContext = useCart();
//   const [productQuantities, setProductQuantities] = useState({});
//   const auth = useAuth();
//   const [showLoginPrompt, setShowLoginPrompt] = useState(false);
//   const [parentCategories, setParentCategories] = useState([]);
//   const [windowWidth, setWindowWidth] = useState(window.innerWidth);

//   const addToCart =
//     cartContext?.addToCart ||
//     ((product) => {
//       console.log("Cart not available, would add:", product);
//       // Still show notification for demo purposes
//       setShowCartNotification(true);
//       setTimeout(() => setShowCartNotification(false), 2000);
//     });

//   // Image size classes
//   const verticalImageSizes = {
//     small: "w-16 h-16",
//     medium: "w-32 h-32",
//     large: "w-40 h-40",
//   };

//   const sliderImageSizes = {
//     small: "w-32 h-32",
//     medium: "w-40 h-40",
//     large: "w-48 h-48",
//   };

//   // Product card style options
//   const cardStyles = {
//     shadow: "shadow-lg",
//     outline: "border border-gray-200",
//     flat: "",
//   };

//   // Product image aspect ratio styles
//   const productImageRatios = {
//     square: "aspect-square",
//     portrait: "aspect-[3/4]",
//     landscape: "aspect-[4/3]",
//     original: "aspect-auto",
//   };

//   // Product info position layout styles
//   const productInfoStyles = {
//     below: "flex flex-col",
//     right: "flex flex-row",
//     left: "flex flex-row-reverse",
//     overlay: "relative",
//   };

//   // Add this to the useEffect that fetches categories data
//   useEffect(() => {
//     const fetchInitialData = async () => {
//       try {
//         // First fetch basic data without images (fast)
//         const categoriesPromise = fetch(apiPath).then((res) => res.json());
//         const productsPromise = fetch(productApiPath).then((res) => res.json());

//         // Add this to fetch parent categories when needed
//         const promises = [categoriesPromise, productsPromise];
//         if (content.showParentCategories) {
//           // Fetch all parent categories if showAllParentCategories is true,
//           // otherwise only fetch selected ones
//           const parentCategoryUrl = content.showAllParentCategories
//             ? "/api/parent-categories"
//             : `/api/parent-categories/with-images?ids=${encodeURIComponent(
//                 JSON.stringify(content.selectedParentCategoryIds || [])
//               )}`;

//           promises.push(fetch(parentCategoryUrl).then((res) => res.json()));
//         }

//         const results = await Promise.all(promises);
//         const categoriesRes = results[0];
//         const productsRes = results[1];

//         // Process parent categories if needed
//         if (content.showParentCategories && results.length > 2) {
//           setParentCategories(results[2]);
//         }

//         // Rest of your existing code...
//         setCategories(
//           categoriesRes.sort((a, b) => {
//             if (a.order !== undefined && b.order !== undefined) {
//               return a.order - b.order;
//             } else if (a.order !== undefined) {
//               return -1;
//             } else if (b.order !== undefined) {
//               return 1;
//             } else {
//               return a.name.localeCompare(b.name);
//             }
//           })
//         );

//         setProducts(productsRes);
//         setLoading(false);

//         // Fetch images as before...
//       } catch (error) {
//         console.error("Error fetching data:", error);
//         setLoading(false);
//       }
//     };

//     fetchInitialData();
//   }, [apiPath, productApiPath, content.showParentCategories]);

//   // Add this helper function to get child categories for a specific parent
//   const getCategoriesByParent = (parentId) => {
//     return categories.filter(
//       (category) =>
//         category.parentCategory &&
//         (category.parentCategory._id === parentId ||
//           (typeof category.parentCategory === "string" &&
//             category.parentCategory === parentId))
//     );
//   };

//   // Set a default selected category if none is set and categories are loaded
//   useEffect(() => {
//     if (
//       !selectedCategory &&
//       categories.length > 0 &&
//       window.innerWidth >= 768
//     ) {
//       // Find first category that's in the selected categories list (if applicable)
//       let categoryToSelect = categories[0];
//       if (!showAllCategories && selectedCategoryIds.length > 0) {
//         const firstAvailableCategory = categories.find((cat) =>
//           selectedCategoryIds.includes(cat._id)
//         );
//         if (firstAvailableCategory) {
//           categoryToSelect = firstAvailableCategory;
//         }
//       }
//       setSelectedCategory(categoryToSelect._id);
//     }
//   }, [selectedCategory, categories, showAllCategories, selectedCategoryIds]);

//   // Reset visible count when a new category is selected
//   useEffect(() => {
//     setVisibleCount(
//       window.innerWidth < 768 ? loadIncrementMobile : loadIncrementDesktop
//     );
//   }, [selectedCategory, loadIncrementMobile, loadIncrementDesktop]);

//   // useEffect to initialize quantities
//   useEffect(() => {
//     // Initialize with default quantities when products load
//     if (products.length > 0) {
//       const initialQuantities = {};
//       products.forEach((product) => {
//         let defaultQty = defaultQuantity;
//         if (priceDisplayType === "wholesale" && !defaultQty) {
//           defaultQty = 50;
//         } else if (priceDisplayType === "regular" && !defaultQty) {
//           defaultQty = 1;
//         }
//         initialQuantities[product._id] = defaultQty;
//       });
//       setProductQuantities(initialQuantities);
//     }
//   }, [products, defaultQuantity, priceDisplayType]);

//   // Function to handle product click
//   const handleProductClick = (product) => {
//     if (productClickBehavior === "popup") {
//       setSelectedProduct(product);
//       setShowProductPopup(true);
//     } else {
//       navigateToProduct(product);
//     }
//   };

//   // Navigate to product detail page
//   const navigateToProduct = (product) => {
//     const match = product.name.match(/^(.*?)(\d.*)?$/);
//     let baseName = match[1] ? match[1].trim() : product.name;
//     let sizePart = match[2] ? match[2].trim() : "";

//     const slugBase = baseName
//       .toLowerCase()
//       .replace(/\s+/g, "-")
//       .replace(/[^a-z0-9-]/g, "");

//     const slugSize = sizePart;
//     const productSlug = slugSize ? `${slugBase}-${slugSize}` : slugBase;

//     window.location.href = `/product/${productSlug}`;
//   };

//   // Function to check if user can see prices
//   const canSeePrice = () => {
//     if (priceVisibility === "all") return true;
//     if (!auth.user) return false; // Not logged in
//     if (priceVisibility === "authenticated") return true; // Any logged in user
//     if (priceVisibility === "business")
//       return auth.user.userType === "business";
//     if (priceVisibility === "regular") return auth.user.userType === "regular";
//     return false; // 'none' or any other value
//   };

//   // Function to check if user can add to cart
//   const canAddToCart = () => {
//     if (cartVisibility === "all") return true;
//     if (!auth.user) return false; // Not logged in
//     if (cartVisibility === "authenticated") return true; // Any logged in user
//     if (cartVisibility === "business") return auth.user.userType === "business";
//     if (cartVisibility === "regular") return auth.user.userType === "regular";
//     return false; // 'none' or any other value
//   };

//   // Function to handle unauthorized cart actions
//   const handleUnauthorizedCartAction = () => {
//     setShowLoginPrompt(true);
//     setTimeout(() => setShowLoginPrompt(false), 3000);
//   };

//   // Scroll horizontally in the category slider
//   const scroll = (direction) => {
//     const container = categorySliderRef.current;
//     if (container) {
//       const scrollAmount = direction === "left" ? -300 : 300;
//       container.scrollBy({ left: scrollAmount, behavior: "smooth" });
//     }
//   };

//   // Add product to cart
//   const handleAddToCart = async (product) => {
//     try {
//       if (!canAddToCart()) {
//         handleUnauthorizedCartAction();
//         return;
//       }

//       // const token = localStorage.getItem("token");
//       // if (!token) {
//       //   window.location.href = "/login";
//       //   return;
//       // }

//       // Use the selected quantity or default
//       const quantity = productQuantities[product._id] || defaultQuantity || 1;

//       await addToCart({
//         product,
//         quantity: quantity,
//         customization: {
//           preview: product.images[0]?.data || "",
//           description: "",
//         },
//       });

//       // Show notification and scroll to top
//       setShowCartNotification(true);
//       window.scrollTo({ top: 0, behavior: "smooth" });
//       setTimeout(() => setShowCartNotification(false), 2000);
//     } catch (error) {
//       console.error("Error adding to cart:", error);
//     }
//   };

//   // Fetch categories and products data
//   useEffect(() => {
//     const fetchInitialData = async () => {
//       try {
//         // First fetch basic data without images (fast)
//         const [categoriesRes, productsRes] = await Promise.all([
//           fetch(apiPath).then((res) => res.json()),
//           fetch(productApiPath).then((res) => res.json()),
//         ]);

//         // Sort categories by order if available
//         // setCategories(categoriesRes.sort((a, b) => a.order - b.order));
//         setCategories(
//           categoriesRes.sort((a, b) => {
//             // If both have order property, sort by order
//             if (a.order !== undefined && b.order !== undefined) {
//               return a.order - b.order;
//             }
//             // If only a has order, it comes first
//             else if (a.order !== undefined) {
//               return -1;
//             }
//             // If only b has order, it comes first
//             else if (b.order !== undefined) {
//               return 1;
//             }
//             // If neither has order, sort by name
//             else {
//               return a.name.localeCompare(b.name);
//             }
//           })
//         );

//         setProducts(productsRes);
//         setLoading(false);

//         // Then fetch images in parallel
//         const productImagePromises = productsRes.map((product) =>
//           fetch(`/api/products/${product._id}/image`)
//             .then((res) => res.json())
//             .then((imageData) => {
//               setProducts((prev) =>
//                 prev.map((p) =>
//                   p._id === product._id ? { ...p, images: [imageData] } : p
//                 )
//               );
//             })
//             .catch((error) =>
//               console.error(
//                 `Error loading image for product ${product._id}:`,
//                 error
//               )
//             )
//         );

//         const categoryImagePromises = categoriesRes.map((category) =>
//           fetch(`/api/categories/${category._id}/image`)
//             .then((res) => res.json())
//             .then((imageData) => {
//               setCategories((prev) =>
//                 prev.map((c) =>
//                   c._id === category._id ? { ...c, image: imageData } : c
//                 )
//               );
//             })
//             .catch((error) =>
//               console.error(
//                 `Error loading image for category ${category._id}:`,
//                 error
//               )
//             )
//         );

//         // Load all images in parallel
//         await Promise.allSettled([
//           ...productImagePromises,
//           ...categoryImagePromises,
//         ]);
//       } catch (error) {
//         console.error("Error fetching data:", error);
//         setLoading(false);
//       }
//     };

//     fetchInitialData();
//   }, [apiPath, productApiPath]);

//   // Filter categories based on search input
//   // const filteredCategories = categories.filter(category =>
//   //   category.name.toLowerCase().includes(categorySearchTerm.toLowerCase())
//   // );

//   // const filteredCategories = [...categories].filter(category =>
//   //   category.name.toLowerCase().includes(categorySearchTerm.toLowerCase())
//   // );
//   const filteredCategories = [...categories].filter(
//     (category) =>
//       // Filter by search term
//       category.name.toLowerCase().includes(categorySearchTerm.toLowerCase()) &&
//       // Filter by selected categories if showAllCategories is false
//       (showAllCategories || selectedCategoryIds.includes(category._id))
//   );

//   // Scroll to category element after loading (mobile view)
//   const scrollToCategory = (categoryId) => {
//     setTimeout(() => {
//       const element = document.getElementById(`category-${categoryId}`);
//       if (element) {
//         const elementPosition = element.getBoundingClientRect().top;
//         const offsetPosition = elementPosition + window.pageYOffset - 60; // 60px offset from top
//         window.scrollTo({
//           top: offsetPosition,
//           behavior: "smooth",
//         });
//       }
//     }, 100);
//   };

//   // Calculate grid column classes
//   const getGridColumns = () => {
//     const mobile = productGridColumnsResponsive?.mobile || 1;
//     const tablet = productGridColumnsResponsive?.tablet || 2;
//     const desktop = productGridColumnsResponsive?.desktop || 3;

//     return `grid-cols-${mobile} sm:grid-cols-${tablet} lg:grid-cols-${desktop}`;
//   };

//   return (
//     <div
//       className="container mx-auto px-4 pt-24 mb-32 min-h-screen"
//       style={{ backgroundColor }}
//     >
//       {/* Loading indicator */}
//       {loading && (
//         // <div className="text-center py-12">
//         //   <p className="text-xl text-gray-600">Loading products...</p>
//         // </div>
//         //<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
//         <div className="fixed inset-0 flex flex-col items-center justify-center">
//           <Lottie
//             animationData={require("../../../assets/loading-product.json")}
//             loop={true}
//             className="h-64 w-64"
//           />
//         </div>
//       )}

//       {/* Show message when no category is selected */}
//       {/* {!selectedCategory && !loading && (
//         <div className="text-center py-12">
//           <Lottie animationData={require('../../../assets/loading-product.json')} loop={true} className="h-64 w-64" />
//         </div>
//       )} */}

//       {/* Cart Notification */}
//       {showCartNotification && (
//         <div className="fixed inset-0 flex items-center justify-center z-50 transition-all duration-300 ease-in-out animate-notification">
//           <div className="bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg flex flex-col items-center space-y-4">
//             {typeof Lottie !== "undefined" && cartAnimation && (
//               <Lottie
//                 animationData={cartAnimation}
//                 loop={false}
//                 className="h-24 w-24"
//               />
//             )}
//             <p className="text-xl">Item added to cart successfully!</p>
//           </div>
//         </div>
//       )}

//       {/* Mobile Loading Indicator */}
//       {showFullPageLoading && (
//         <div className="fixed inset-0 bg-white bg-opacity-70 backdrop-blur-sm flex justify-center items-center z-50">
//           {showLoadingAnimation && loadingAnimationUrl && (
//             <img src={loadingAnimationUrl} alt="Loading..." />
//           )}
//         </div>
//       )}

//       {/* Section Header */}
//       <div className="flex justify-between items-center mb-12">
//         <div>
//           {title && (
//             <h2 className="text-3xl font-serif font-bold text-gray-900">
//               {title}
//             </h2>
//           )}
//           {description && (
//             <p className="text-lg text-gray-600 mt-2">{description}</p>
//           )}
//         </div>
//         {/* {searchEnabled && (
//           <div className="relative w-64">
//             <input
//               type="text"
//               placeholder={searchPlaceholder}
//               value={categorySearchTerm}
//               onChange={(e) => setCategorySearchTerm(e.target.value)}
//               className="w-full px-4 py-2 pl-10 rounded-full border-2 border-gray-300 focus:outline-none focus:border-blue-500"
//             />
//             <Search
//               className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
//               size={16}
//             />
//           </div>
//         )} */}
//       </div>

//       {filteredCategories.length < sliderThreshold ? (
//         <>
//           {/* Desktop/Tablet Vertical Layout */}
//           <div
//             className="hidden md:flex flex-col md:flex-row gap-6"
//             style={{ overflowX: "hidden" }}
//           >
//             {/* Left Column: Category or Parent Category List with Images */}
//             <div className="w-full md:w-1/3">
//               {content.showParentCategories
//                 ? // Parent Categories Mode
//                   parentCategories
//                     .filter(
//                       (parent) =>
//                         showAllParentCategories ||
//                         (selectedParentCategoryIds &&
//                           selectedParentCategoryIds.includes(parent._id))
//                     )
//                     .map((parent) => (
//                       <div key={parent._id} className="mb-6">
//                         {/* Modified parent category rendering */}
//                         <div
//                           onClick={() =>
//                             setSelectedCategory(parent._id + "_parent")
//                           }
//                           className={`flex items-center p-4 border-b border-gray-200 cursor-pointer category-item`}
//                           style={{
//                             backgroundColor:
//                               selectedCategory === parent._id + "_parent"
//                                 ? selectedCategoryBackgroundColor || "#60a5fa"
//                                 : categoryBackgroundColor || "#f3f4f6",
//                             color:
//                               selectedCategory === parent._id + "_parent"
//                                 ? selectedCategoryTextColor || "#ffffff"
//                                 : categoryTextColor || "#1f2937",
//                           }}
//                         >
//                           {parentCategoryDisplayStyle !== "nameOnly" && (
//                             <div
//                               className={`${
//                                 verticalImageSizes[verticalImageSize]
//                               } ${
//                                 categoryImageShape === "circle"
//                                   ? "rounded-full"
//                                   : "rounded-lg"
//                               } overflow-hidden border-4 mr-4`}
//                               style={{
//                                 borderColor:
//                                   selectedCategory === parent._id + "_parent"
//                                     ? selectedCategoryBorderColor
//                                     : categoryBorderColor,
//                               }}
//                             >
//                               <LazyImage
//                                 src={parent.image?.data}
//                                 alt={parent.name}
//                                 className="w-full h-full object-cover"
//                               />
//                             </div>
//                           )}
//                           <p className="font-medium text-lg">{parent.name}</p>
//                         </div>

//                         {/* Child Categories Row */}
//                         <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 pl-4 py-2">
//                           {getCategoriesByParent(parent._id).map((category) => (
//                             <div
//                               key={category._id}
//                               onClick={() => setSelectedCategory(category._id)}
//                               className={`flex-none mr-3 cursor-pointer`}
//                             >
//                               <div
//                                 className={`${
//                                   content.childCategoryImageSize === "tiny"
//                                     ? "w-12 h-12 min-h-12 max-h-12"
//                                     : content.childCategoryImageSize === "small"
//                                     ? "w-18 h-18 min-h-18 max-h-18"
//                                     : "w-24 h-24 min-h-24 max-h-24"
//                                 } ${
//                                   categoryImageShape === "circle"
//                                     ? "rounded-full"
//                                     : "rounded-lg"
//                                 } overflow-hidden border-2 mb-1`}
//                                 style={{
//                                   borderColor:
//                                     selectedCategory === category._id
//                                       ? selectedCategoryBorderColor
//                                       : categoryBorderColor,
//                                 }}
//                               >
//                                 <LazyImage
//                                   src={category.image?.data}
//                                   alt={category.name}
//                                   className="w-full h-full object-cover"
//                                 />
//                               </div>
//                               <p
//                                 className="text-xs font-medium text-center"
//                                 style={{
//                                   maxWidth:
//                                     content.childCategoryImageSize === "tiny"
//                                       ? "50px"
//                                       : content.childCategoryImageSize ===
//                                         "small"
//                                       ? "74px"
//                                       : "96px",
//                                 }}
//                               >
//                                 {category.name}
//                               </p>
//                             </div>
//                           ))}
//                         </div>
//                       </div>
//                     ))
//                 : // Regular Categories Mode
//                   filteredCategories.map((category) => (
//                     <div
//                       key={category._id}
//                       onClick={() => setSelectedCategory(category._id)}
//                       className={`flex items-center p-4 border-b border-gray-200 cursor-pointer category-item`}
//                       style={{
//                         backgroundColor:
//                           selectedCategory === category._id
//                             ? selectedCategoryBackgroundColor || "#60a5fa"
//                             : categoryBackgroundColor || "#f3f4f6",
//                         color:
//                           selectedCategory === category._id
//                             ? selectedCategoryTextColor || "#ffffff"
//                             : categoryTextColor || "#1f2937",
//                       }}
//                     >
//                       <div
//                         className={`${verticalImageSizes[verticalImageSize]} ${
//                           categoryImageShape === "circle"
//                             ? "rounded-full"
//                             : "rounded-lg"
//                         } overflow-hidden border-4 mr-4`}
//                         style={{
//                           borderColor:
//                             selectedCategory === category._id
//                               ? selectedCategoryBorderColor
//                               : categoryBorderColor,
//                         }}
//                       >
//                         <LazyImage
//                           src={category.image?.data}
//                           alt={category.name}
//                           className="w-full h-full object-cover"
//                         />
//                       </div>
//                       <p className="font-medium text-lg">{category.name}</p>
//                     </div>
//                   ))}
//             </div>

//             {/* Right Column: Related Products for the Selected Category */}
//             <div className="w-full md:w-2/3 pb-6">
//               {selectedCategory ? (
//                 <>
//                   <div
//                     key={selectedCategory}
//                     className={`grid ${getGridColumns()} gap-6 ${
//                       fadeEffectEnabled ? "fade-in" : ""
//                     } pb-4`}
//                   >
//                     {products
//                       .filter((product) => {
//                         // Handle parent and regular category selection
//                         if (selectedCategory.includes("_parent")) {
//                           // For parent categories, show products from all child categories
//                           const parentId = selectedCategory.replace(
//                             "_parent",
//                             ""
//                           );
//                           const childCategoryIds = getCategoriesByParent(
//                             parentId
//                           ).map((c) => c._id);
//                           return childCategoryIds.some(
//                             (childId) =>
//                               product.category?._id === childId ||
//                               product.category?.__id === childId
//                           );
//                         } else {
//                           // Regular category filtering
//                           return (
//                             product.category?.__id === selectedCategory ||
//                             product.category?._id === selectedCategory
//                           );
//                         }
//                       })
//                       .slice(0, visibleCount)
//                       .map((product) => (
//                         <div
//                           key={product._id}
//                           className={`bg-white rounded-lg ${cardStyles[productCardStyle]} overflow-hidden transition-transform hover:scale-105 ${productInfoStyles[productInfoPosition]}`}
//                         >
//                           {/* Product image */}
//                           <div
//                             className={`relative ${
//                               productInfoPosition === "below" ||
//                               productInfoPosition === "overlay"
//                                 ? "w-full"
//                                 : productInfoPosition === "right"
//                                 ? "w-1/2"
//                                 : "order-2 w-1/2"
//                             } cursor-pointer ${
//                               productImageRatios[productImageAspectRatio]
//                             }`}
//                             onClick={() => handleProductClick(product)}
//                           >
//                             <LazyImage
//                               src={product.images?.[0]?.data}
//                               alt={product.name}
//                               className="absolute inset-0 w-full h-full object-cover"
//                             />

//                             {/* Overlay product info if that position is selected */}
//                             {productInfoPosition === "overlay" && (
//                               <div className="absolute inset-0 bg-black bg-opacity-40 flex flex-col justify-end p-4">
//                                 {productDisplayFields.showName !== false && (
//                                   <h3
//                                     className={`font-bold mb-1 text-${productTextAlignment} text-white`}
//                                     style={{
//                                       WebkitLineClamp:
//                                         productNameLines > 0
//                                           ? productNameLines
//                                           : "none",
//                                       display:
//                                         productNameLines > 0
//                                           ? "-webkit-box"
//                                           : "block",
//                                       WebkitBoxOrient: "vertical",
//                                       overflow: "hidden",
//                                     }}
//                                   >
//                                     {product.name}
//                                   </h3>
//                                 )}

//                                 {/* {productDisplayFields.showPrice !== false && (
//                                   <div className={`flex flex-col ${productTextAlignment === 'center' ? 'items-center' : productTextAlignment === 'right' ? 'items-end' : 'items-start'}`}>
//                                     <span className="text-lg font-bold text-green-600">
//                                       {priceDisplayType === 'tier' && product.pricingTiers && product.pricingTiers.length > 0 ?
//                                         `$${product.pricingTiers[product.pricingTiers.length - 1].price.toFixed(2)}` :
//                                       priceDisplayType === 'wholesale' && product.basePrice ?
//                                         `$${product.basePrice.toFixed(2)}` :
//                                         `$${product.regularPrice ? product.regularPrice.toFixed(2) : product.basePrice.toFixed(2)}`}
//                                     </span>

//                                     {priceDisplayType === 'tier' && product.pricingTiers && product.pricingTiers.length > 0 && (
//                                       <span className="text-xs text-blue-600">
//                                         Tier price
//                                       </span>
//                                     )}
//                                     {priceDisplayType === 'wholesale' && (
//                                       <span className="text-xs text-blue-600">Wholesale price</span>
//                                     )}
//                                     {priceDisplayType === 'regular' && (
//                                       <span className="text-xs text-blue-600">Regular price</span>
//                                     )}
//                                   </div>
//                                 )} */}
//                                 {productDisplayFields.showPrice !== false &&
//                                   (canSeePrice() ? (
//                                     <div
//                                       className={`flex flex-col ${
//                                         productTextAlignment === "center"
//                                           ? "items-center"
//                                           : productTextAlignment === "right"
//                                           ? "items-end"
//                                           : "items-start"
//                                       }`}
//                                     >
//                                       <span className="text-lg font-bold text-green-600">
//                                         {priceDisplayType === "tier" &&
//                                         product.pricingTiers &&
//                                         product.pricingTiers.length > 0
//                                           ? `$${product.pricingTiers[
//                                               product.pricingTiers.length - 1
//                                             ].price.toFixed(2)}`
//                                           : priceDisplayType === "wholesale" &&
//                                             product.basePrice
//                                           ? `$${product.basePrice.toFixed(2)}`
//                                           : `$${
//                                               product.regularPrice
//                                                 ? product.regularPrice.toFixed(
//                                                     2
//                                                   )
//                                                 : product.basePrice.toFixed(2)
//                                             }`}
//                                       </span>

//                                       {priceDisplayType === "tier" &&
//                                         product.pricingTiers &&
//                                         product.pricingTiers.length > 0 && (
//                                           <span className="text-xs text-blue-600">
//                                             Tier price
//                                           </span>
//                                         )}
//                                       {priceDisplayType === "wholesale" && (
//                                         <span className="text-xs text-blue-600">
//                                           Wholesale price
//                                         </span>
//                                       )}
//                                       {priceDisplayType === "regular" && (
//                                         <span className="text-xs text-blue-600">
//                                           Regular price
//                                         </span>
//                                       )}
//                                     </div>
//                                   ) : (
//                                     <div className="text-sm italic text-gray-600">
//                                       {loginPromptMessage}
//                                     </div>
//                                   ))}

//                                 {productDisplayFields.showDescription &&
//                                   product.description && (
//                                     <p
//                                       className={`text-sm text-gray-200 text-${productTextAlignment}`}
//                                       style={{
//                                         WebkitLineClamp:
//                                           productDescriptionLines > 0
//                                             ? productDescriptionLines
//                                             : "none",
//                                         display:
//                                           productDescriptionLines > 0
//                                             ? "-webkit-box"
//                                             : "block",
//                                         WebkitBoxOrient: "vertical",
//                                         overflow: "hidden",
//                                       }}
//                                     >
//                                       {product.description}
//                                     </p>
//                                   )}
//                               </div>
//                             )}
//                           </div>

//                           {/* Product info not in overlay position */}
//                           {productInfoPosition !== "overlay" && (
//                             <div
//                               className={`p-4 ${
//                                 productInfoPosition === "below"
//                                   ? "w-full"
//                                   : productInfoPosition === "right"
//                                   ? "flex-1"
//                                   : productInfoPosition === "left"
//                                   ? "order-1 flex-1"
//                                   : ""
//                               } cursor-pointer`}
//                               onClick={() => handleProductClick(product)}
//                             >
//                               {productDisplayFields.showName !== false && (
//                                 <h3
//                                   className={`font-bold mb-2 text-gray-900 text-${productTextAlignment}`}
//                                   style={{
//                                     WebkitLineClamp:
//                                       productNameLines > 0
//                                         ? productNameLines
//                                         : "none",
//                                     display:
//                                       productNameLines > 0
//                                         ? "-webkit-box"
//                                         : "block",
//                                     WebkitBoxOrient: "vertical",
//                                     overflow: "hidden",
//                                   }}
//                                 >
//                                   {product.name}
//                                 </h3>
//                               )}

//                               {productDisplayFields.showDescription &&
//                                 product.description && (
//                                   <p
//                                     className={`text-sm text-gray-600 mb-2 text-${productTextAlignment}`}
//                                     style={{
//                                       WebkitLineClamp:
//                                         productDescriptionLines > 0
//                                           ? productDescriptionLines
//                                           : "none",
//                                       display:
//                                         productDescriptionLines > 0
//                                           ? "-webkit-box"
//                                           : "block",
//                                       WebkitBoxOrient: "vertical",
//                                       overflow: "hidden",
//                                     }}
//                                   >
//                                     {product.description}
//                                   </p>
//                                 )}

//                               {productDisplayFields.showNote &&
//                                 product.note && (
//                                   <p
//                                     className={`text-sm italic text-gray-500 mb-2 text-${productTextAlignment}`}
//                                   >
//                                     {product.note}
//                                   </p>
//                                 )}

//                               <div
//                                 className={`flex justify-between items-center ${
//                                   productTextAlignment === "center"
//                                     ? "justify-center"
//                                     : productTextAlignment === "right"
//                                     ? "justify-end"
//                                     : ""
//                                 }`}
//                               >
//                                 {/* {productDisplayFields.showPrice !== false && (
//                                   <span className="text-lg font-bold text-blue-600">
//                                     ${product.pricingTiers && product.pricingTiers.length > 0 ?
//                                       product.pricingTiers[product.pricingTiers.length - 1].price.toFixed(2) :
//                                       product.basePrice.toFixed(2)}
//                                   </span>
//                                 )} */}
//                                 {productDisplayFields.showPrice !== false &&
//                                   (canSeePrice() ? (
//                                     <div
//                                       className={`flex flex-col ${
//                                         productTextAlignment === "center"
//                                           ? "items-center"
//                                           : productTextAlignment === "right"
//                                           ? "items-end"
//                                           : "items-start"
//                                       }`}
//                                     >
//                                       <span className="text-lg font-bold text-green-600">
//                                         {priceDisplayType === "tier" &&
//                                         product.pricingTiers &&
//                                         product.pricingTiers.length > 0
//                                           ? `$${product.pricingTiers[
//                                               product.pricingTiers.length - 1
//                                             ].price.toFixed(2)}`
//                                           : priceDisplayType === "wholesale" &&
//                                             product.basePrice
//                                           ? `$${product.basePrice.toFixed(2)}`
//                                           : `$${
//                                               product.regularPrice
//                                                 ? product.regularPrice.toFixed(
//                                                     2
//                                                   )
//                                                 : product.basePrice.toFixed(2)
//                                             }`}
//                                       </span>

//                                       {priceDisplayType === "tier" &&
//                                         product.pricingTiers &&
//                                         product.pricingTiers.length > 0 && (
//                                           <span className="text-xs text-blue-600">
//                                             Tier price
//                                             {/* ({product.pricingTiers[product.pricingTiers.length - 1].minQuantity}+) */}
//                                           </span>
//                                         )}
//                                       {priceDisplayType === "wholesale" && (
//                                         <span className="text-xs text-blue-600">
//                                           Wholesale price
//                                         </span>
//                                       )}
//                                       {priceDisplayType === "regular" && (
//                                         <span className="text-xs text-blue-600">
//                                           Regular price
//                                         </span>
//                                       )}
//                                     </div>
//                                   ) : (
//                                     <div className="text-sm italic text-gray-600">
//                                       {/* {loginPromptMessage} */}
//                                     </div>
//                                   ))}

//                                 {showQuickAction && (
//                                   <div className="flex gap-2">
//                                     <button
//                                       style={{
//                                         boxShadow: "none",
//                                         border: "none",
//                                       }}
//                                       onClick={(e) => {
//                                         e.stopPropagation();
//                                         handleProductClick(product);
//                                       }}
//                                       className="text-gray-600 hover:text-blue-600 transition-colors"
//                                       title={
//                                         quickActionIcon === "customize"
//                                           ? "Customize"
//                                           : "Add to Cart"
//                                       }
//                                     >
//                                       {quickActionIcon === "customize" ? (
//                                         <svg
//                                           xmlns="http://www.w3.org/2000/svg"
//                                           className="h-6 w-6"
//                                           fill="none"
//                                           viewBox="0 0 24 24"
//                                           stroke="currentColor"
//                                         >
//                                           <path
//                                             strokeLinecap="round"
//                                             strokeLinejoin="round"
//                                             strokeWidth={2}
//                                             d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
//                                           />
//                                         </svg>
//                                       ) : (
//                                         <svg
//                                           xmlns="http://www.w3.org/2000/svg"
//                                           className="h-6 w-6"
//                                           fill="none"
//                                           viewBox="0 0 24 24"
//                                           stroke="currentColor"
//                                         >
//                                           <path
//                                             strokeLinecap="round"
//                                             strokeLinejoin="round"
//                                             strokeWidth={2}
//                                             d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
//                                           />
//                                         </svg>
//                                       )}
//                                     </button>
//                                   </div>
//                                 )}
//                               </div>
//                             </div>
//                           )}
//                         </div>
//                       ))}
//                     {selectedCategory.includes("_parent")
//                       ? // For parent category, check if any child category has products
//                         getCategoriesByParent(
//                           selectedCategory.replace("_parent", "")
//                         ).length > 0 &&
//                         !products.some((product) => {
//                           const parentId = selectedCategory.replace(
//                             "_parent",
//                             ""
//                           );
//                           const childCategoryIds = getCategoriesByParent(
//                             parentId
//                           ).map((c) => c._id);
//                           return childCategoryIds.some(
//                             (childId) =>
//                               product.category?._id === childId ||
//                               product.category?.__id === childId
//                           );
//                         }) && (
//                           <p className="text-gray-600 col-span-full text-center py-8">
//                             No products found for this category.
//                           </p>
//                         )
//                       : // For regular category, show message if no products found
//                         !products.some(
//                           (product) =>
//                             product.category?.__id === selectedCategory ||
//                             product.category?._id === selectedCategory
//                         ) && (
//                           <p className="text-gray-600 col-span-full text-center py-8">
//                             No products found for this category.
//                           </p>
//                         )}
//                   </div>

//                   {/* Load more button logic - adjust for parent categories */}
//                   {selectedCategory.includes("_parent")
//                     ? products.filter((product) => {
//                         const parentId = selectedCategory.replace(
//                           "_parent",
//                           ""
//                         );
//                         const childCategoryIds = getCategoriesByParent(
//                           parentId
//                         ).map((c) => c._id);
//                         return childCategoryIds.some(
//                           (childId) =>
//                             product.category?._id === childId ||
//                             product.category?.__id === childId
//                         );
//                       }).length > visibleCount && (
//                         <div className="flex justify-center mt-8">
//                           <button
//                             onClick={() => {
//                               setIsLoadingMore(true);
//                               setTimeout(() => {
//                                 setVisibleCount(
//                                   (prev) => prev + loadIncrementDesktop
//                                 );
//                                 setIsLoadingMore(false);
//                               }, 500);
//                             }}
//                             className="text-white px-7 py-3 rounded hover:opacity-90 transition-colors"
//                             style={{ backgroundColor: loadMoreButtonColor }}
//                           >
//                             {isLoadingMore ? (
//                               showLoadingAnimation ? (
//                                 <svg
//                                   className="animate-spin h-5 w-5 text-white mx-auto"
//                                   xmlns="http://www.w3.org/2000/svg"
//                                   fill="none"
//                                   viewBox="0 0 24 24"
//                                 >
//                                   <circle
//                                     className="opacity-25"
//                                     cx="12"
//                                     cy="12"
//                                     r="10"
//                                     stroke="currentColor"
//                                     strokeWidth="4"
//                                   ></circle>
//                                   <path
//                                     className="opacity-75"
//                                     fill="currentColor"
//                                     d="M4 12a8 8 0 018-8v8H4z"
//                                   ></path>
//                                 </svg>
//                               ) : (
//                                 <Lottie
//                                   animationData={require("../../../assets/loading-product.json")}
//                                   loop={true}
//                                   className="h-64 w-64"
//                                 />
//                               )
//                             ) : (
//                               loadMoreButtonText
//                             )}
//                           </button>
//                         </div>
//                       )
//                     : // Regular category load more button (unchanged)
//                       products.filter(
//                         (product) =>
//                           product.category?.__id === selectedCategory ||
//                           product.category?._id === selectedCategory
//                       ).length > visibleCount && (
//                         <div className="flex justify-center mt-8">
//                           <button
//                             onClick={() => {
//                               setIsLoadingMore(true);
//                               setTimeout(() => {
//                                 setVisibleCount(
//                                   (prev) => prev + loadIncrementDesktop
//                                 );
//                                 setIsLoadingMore(false);
//                               }, 500);
//                             }}
//                             className="text-white px-7 py-3 rounded hover:opacity-90 transition-colors"
//                             style={{ backgroundColor: loadMoreButtonColor }}
//                           >
//                             {isLoadingMore ? (
//                               showLoadingAnimation ? (
//                                 <svg
//                                   className="animate-spin h-5 w-5 text-white mx-auto"
//                                   xmlns="http://www.w3.org/2000/svg"
//                                   fill="none"
//                                   viewBox="0 0 24 24"
//                                 >
//                                   <circle
//                                     className="opacity-25"
//                                     cx="12"
//                                     cy="12"
//                                     r="10"
//                                     stroke="currentColor"
//                                     strokeWidth="4"
//                                   ></circle>
//                                   <path
//                                     className="opacity-75"
//                                     fill="currentColor"
//                                     d="M4 12a8 8 0 018-8v8H4z"
//                                   ></path>
//                                 </svg>
//                               ) : (
//                                 <Lottie
//                                   animationData={require("../../../assets/loading-product.json")}
//                                   loop={true}
//                                   className="h-64 w-64"
//                                 />
//                               )
//                             ) : (
//                               loadMoreButtonText
//                             )}
//                           </button>
//                         </div>
//                       )}
//                 </>
//               ) : (
//                 <p className="text-gray-600">{/* No Categories Found */}</p>
//               )}
//             </div>
//           </div>

//           {/* Mobile Layout */}
//           <div className="md:hidden space-y-6 px-0">
//             {/* If showParentCategories is true and we have parent categories, display them with their children */}
//             {content.showParentCategories &&
//               parentCategories
//                 .filter(
//                   (parent) =>
//                     showAllParentCategories ||
//                     (selectedParentCategoryIds &&
//                       selectedParentCategoryIds.includes(parent._id))
//                 )
//                 .map((parent) => (
//                   <div key={parent._id + "-mobile"} className="mb-8">
//                     {/* Parent Category Heading */}
//                     <div
//                       className="font-bold text-xl mb-3 px-4 py-2 bg-gray-100 rounded-lg"
//                       style={{
//                         backgroundColor: categoryBackgroundColor || "#f3f4f6",
//                         color: categoryTextColor || "#1f2937",
//                       }}
//                     >
//                       {parent.name}
//                     </div>

//                     {/* Child Categories for this parent */}
//                     {getCategoriesByParent(parent._id).map((category) => (
//                       <div key={category._id}>
//                         <div
//                           style={{
//                             marginLeft: -17 + "px",
//                             marginRight: -16 + "px",
//                             overflow: "hidden",
//                             overflowX: "hidden",
//                           }}
//                         >
//                           <div
//                             id={`category-${category._id}`}
//                             onClick={() => {
//                               if (window.innerWidth < 768) {
//                                 setShowFullPageLoading(true);
//                                 setTimeout(() => {
//                                   setSelectedCategory(category._id);
//                                   setShowFullPageLoading(false);
//                                   scrollToCategory(category._id);
//                                 }, 500);
//                               } else {
//                                 setSelectedCategory(category._id);
//                               }
//                             }}
//                             className={`flex items-center p-4 border border-gray-200 cursor-pointer category-item`}
//                             style={{
//                               backgroundColor:
//                                 selectedCategory === category._id
//                                   ? selectedCategoryBackgroundColor || "#60a5fa"
//                                   : categoryBackgroundColor || "#f3f4f6",
//                               color:
//                                 selectedCategory === category._id
//                                   ? selectedCategoryTextColor || "#ffffff"
//                                   : categoryTextColor || "#1f2937",
//                             }}
//                           >
//                             <div
//                               className={`${verticalImageSizes.small} ${
//                                 categoryImageShape === "circle"
//                                   ? "rounded-full"
//                                   : "rounded-lg"
//                               } overflow-hidden border-4 mr-4`}
//                               style={{
//                                 borderColor:
//                                   selectedCategory === category._id
//                                     ? selectedCategoryBorderColor
//                                     : categoryBorderColor,
//                               }}
//                             >
//                               <LazyImage
//                                 src={category.image?.data}
//                                 alt={category.name}
//                                 className="w-full h-full object-cover"
//                               />
//                             </div>
//                             <p className="font-medium text-lg">
//                               {category.name}
//                             </p>
//                           </div>
//                         </div>
//                         {selectedCategory === category._id &&
//                           (isLoadingMore ? (
//                             <div className="flex justify-center items-center py-2">
//                               {showLoadingAnimation ? (
//                                 <svg
//                                   className="animate-spin h-6 w-6 text-blue-600"
//                                   xmlns="http://www.w3.org/2000/svg"
//                                   fill="none"
//                                   viewBox="0 0 24 24"
//                                 >
//                                   <circle
//                                     className="opacity-25"
//                                     cx="12"
//                                     cy="12"
//                                     r="10"
//                                     stroke="currentColor"
//                                     strokeWidth="4"
//                                   ></circle>
//                                   <path
//                                     className="opacity-75"
//                                     fill="currentColor"
//                                     d="M4 12a8 8 0 018-8v8H4z"
//                                   ></path>
//                                 </svg>
//                               ) : (
//                                 <p>Loading...</p>
//                               )}
//                             </div>
//                           ) : (
//                             <div
//                               id={`category-products-${category._id}`}
//                               className="mt-4 mb-4"
//                             >
//                               <div className={`grid ${getGridColumns()} gap-6`}>
//                                 {products
//                                   .filter((product) =>
//                                     product.category?.__id
//                                       ? product.category.__id === category._id
//                                       : product.category?._id === category._id
//                                   )
//                                   .slice(0, visibleCount)
//                                   .map((product) => (
//                                     <div
//                                       key={product._id}
//                                       className={`bg-white rounded-lg ${cardStyles[productCardStyle]} overflow-hidden transition-transform hover:scale-105 ${productInfoStyles[productInfoPosition]}`}
//                                     >
//                                       <div
//                                         className={`relative ${
//                                           productInfoPosition === "below" ||
//                                           productInfoPosition === "overlay"
//                                             ? "w-full"
//                                             : "w-1/2 min-w-1/2"
//                                         } ${
//                                           productImageRatios[
//                                             productImageAspectRatio
//                                           ]
//                                         } cursor-pointer`}
//                                         onClick={() =>
//                                           handleProductClick(product)
//                                         }
//                                       >
//                                         <LazyImage
//                                           src={product.images?.[0]?.data}
//                                           alt={product.name}
//                                           className="absolute inset-0 w-full h-full object-cover"
//                                         />
//                                       </div>
//                                       <div
//                                         className={`p-4 cursor-pointer ${
//                                           productInfoPosition === "below" ||
//                                           productInfoPosition === "overlay"
//                                             ? "w-full"
//                                             : "w-1/2 max-w-1/2"
//                                         }
//                                          `}
//                                         onClick={() =>
//                                           handleProductClick(product)
//                                         }
//                                       >
//                                         {productDisplayFields.showName !==
//                                           false && (
//                                           <h3
//                                             className={`text-md font-bold text-gray-900 mb-2 text-${productTextAlignment}`}
//                                           >
//                                             {product.name}
//                                           </h3>
//                                         )}
//                                         <div
//                                           className={`flex justify-between items-center ${
//                                             productTextAlignment === "center"
//                                               ? "justify-center"
//                                               : productTextAlignment === "right"
//                                               ? "justify-end"
//                                               : ""
//                                           }`}
//                                         >
//                                           {productDisplayFields.showPrice !==
//                                             false && (
//                                             <div
//                                               className={`flex flex-col ${
//                                                 productTextAlignment ===
//                                                 "center"
//                                                   ? "items-center"
//                                                   : productTextAlignment ===
//                                                     "right"
//                                                   ? "items-end"
//                                                   : "items-start"
//                                               }`}
//                                             >
//                                               <span className="text-lg font-bold text-green-600">
//                                                 {priceDisplayType === "tier" &&
//                                                 product.pricingTiers &&
//                                                 product.pricingTiers.length > 0
//                                                   ? `$${product.pricingTiers[
//                                                       product.pricingTiers
//                                                         .length - 1
//                                                     ].price.toFixed(2)}`
//                                                   : priceDisplayType ===
//                                                       "wholesale" &&
//                                                     product.basePrice
//                                                   ? `$${product.basePrice.toFixed(
//                                                       2
//                                                     )}`
//                                                   : `$${
//                                                       product.regularPrice
//                                                         ? product.regularPrice.toFixed(
//                                                             2
//                                                           )
//                                                         : product.basePrice.toFixed(
//                                                             2
//                                                           )
//                                                     }`}
//                                               </span>

//                                               {priceDisplayType === "tier" &&
//                                                 product.pricingTiers &&
//                                                 product.pricingTiers.length >
//                                                   0 && (
//                                                   <span className="text-xs text-blue-600">
//                                                     Tier price
//                                                   </span>
//                                                 )}
//                                               {priceDisplayType ===
//                                                 "wholesale" && (
//                                                 <span className="text-xs text-blue-600">
//                                                   Wholesale price
//                                                 </span>
//                                               )}
//                                               {priceDisplayType ===
//                                                 "regular" && (
//                                                 <span className="text-xs text-blue-600">
//                                                   Regular price
//                                                 </span>
//                                               )}
//                                             </div>
//                                           )}
//                                           {showQuickAction && (
//                                             <button
//                                               style={{
//                                                 boxShadow: "none",
//                                                 border: "none",
//                                               }}
//                                               onClick={(e) => {
//                                                 e.stopPropagation();
//                                                 handleProductClick(product);
//                                               }}
//                                               className="text-gray-600 hover:text-blue-600 transition-colors"
//                                               title={
//                                                 quickActionIcon === "customize"
//                                                   ? "Customize"
//                                                   : "Add to Cart"
//                                               }
//                                             >
//                                               {quickActionIcon ===
//                                               "customize" ? (
//                                                 <svg
//                                                   xmlns="http://www.w3.org/2000/svg"
//                                                   className="h-6 w-6"
//                                                   fill="none"
//                                                   viewBox="0 0 24 24"
//                                                   stroke="currentColor"
//                                                 >
//                                                   <path
//                                                     strokeLinecap="round"
//                                                     strokeLinejoin="round"
//                                                     strokeWidth={2}
//                                                     d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
//                                                   />
//                                                 </svg>
//                                               ) : (
//                                                 <svg
//                                                   xmlns="http://www.w3.org/2000/svg"
//                                                   className="h-6 w-6"
//                                                   fill="none"
//                                                   viewBox="0 0 24 24"
//                                                   stroke="currentColor"
//                                                 >
//                                                   <path
//                                                     strokeLinecap="round"
//                                                     strokeLinejoin="round"
//                                                     strokeWidth={2}
//                                                     d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
//                                                   />
//                                                 </svg>
//                                               )}
//                                             </button>
//                                           )}
//                                         </div>
//                                       </div>
//                                     </div>
//                                   ))}
//                                 {products.filter((product) =>
//                                   product.category?.__id
//                                     ? product.category.__id === category._id
//                                     : product.category?._id === category._id
//                                 ).length === 0 && (
//                                   <p className="text-gray-600 col-span-full text-center py-4">
//                                     No products found for this category.
//                                   </p>
//                                 )}
//                               </div>
//                               {products.filter((product) =>
//                                 product.category?.__id
//                                   ? product.category.__id === category._id
//                                   : product.category?._id === category._id
//                               ).length > visibleCount && (
//                                 <div className="flex justify-center mt-8">
//                                   <button
//                                     onClick={() => {
//                                       setIsLoadingMore(true);
//                                       setTimeout(() => {
//                                         setVisibleCount(
//                                           visibleCount + loadIncrementMobile
//                                         );
//                                         setIsLoadingMore(false);
//                                       }, 500);
//                                     }}
//                                     className="text-white px-7 py-3 rounded hover:opacity-90 transition-colors"
//                                     style={{
//                                       backgroundColor: loadMoreButtonColor,
//                                     }}
//                                   >
//                                     {isLoadingMore ? (
//                                       showLoadingAnimation ? (
//                                         <svg
//                                           className="animate-spin h-5 w-5 text-white mx-auto"
//                                           xmlns="http://www.w3.org/2000/svg"
//                                           fill="none"
//                                           viewBox="0 0 24 24"
//                                         >
//                                           <circle
//                                             className="opacity-25"
//                                             cx="12"
//                                             cy="12"
//                                             r="10"
//                                             stroke="currentColor"
//                                             strokeWidth="4"
//                                           ></circle>
//                                           <path
//                                             className="opacity-75"
//                                             fill="currentColor"
//                                             d="M4 12a8 8 0 018-8v8H4z"
//                                           ></path>
//                                         </svg>
//                                       ) : (
//                                         "Loading..."
//                                       )
//                                     ) : (
//                                       loadMoreButtonText
//                                     )}
//                                   </button>
//                                 </div>
//                               )}
//                             </div>
//                           ))}
//                       </div>
//                     ))}
//                   </div>
//                 ))}

//             {/* Regular categories (keep existing rendering) */}
//             {(!content.showParentCategories ||
//               filteredCategories.filter((category) => !category.parentCategory)
//                 .length > 0) &&
//               filteredCategories
//                 .filter(
//                   (category) =>
//                     !content.showParentCategories || !category.parentCategory
//                 )
//                 .map((category) => (
//                   <div key={category._id}>
//                     <div
//                       style={{
//                         marginLeft: -17 + "px",
//                         marginRight: -16 + "px",
//                         overflow: "hidden",
//                         overflowX: "hidden",
//                       }}
//                     >
//                       <div
//                         id={`category-${category._id}`}
//                         onClick={() => {
//                           if (window.innerWidth < 768) {
//                             setShowFullPageLoading(true);
//                             setTimeout(() => {
//                               setSelectedCategory(category._id);
//                               setShowFullPageLoading(false);
//                               scrollToCategory(category._id);
//                             }, 500);
//                           } else {
//                             setSelectedCategory(category._id);
//                           }
//                         }}
//                         className={`flex items-center p-4 border border-gray-200 cursor-pointer category-item`}
//                         style={{
//                           backgroundColor:
//                             selectedCategory === category._id
//                               ? selectedCategoryBackgroundColor || "#60a5fa"
//                               : categoryBackgroundColor || "#f3f4f6",
//                           color:
//                             selectedCategory === category._id
//                               ? selectedCategoryTextColor || "#ffffff"
//                               : categoryTextColor || "#1f2937",
//                         }}
//                       >
//                         <div
//                           className={`${verticalImageSizes.small} ${
//                             categoryImageShape === "circle"
//                               ? "rounded-full"
//                               : "rounded-lg"
//                           } overflow-hidden border-4 mr-4`}
//                           style={{
//                             borderColor:
//                               selectedCategory === category._id
//                                 ? selectedCategoryBorderColor
//                                 : categoryBorderColor,
//                           }}
//                         >
//                           <LazyImage
//                             src={category.image?.data}
//                             alt={category.name}
//                             className="w-full h-full object-cover"
//                           />
//                         </div>
//                         <p className="font-medium text-lg">{category.name}</p>
//                       </div>
//                     </div>
//                     {selectedCategory === category._id &&
//                       (isLoadingMore ? (
//                         <div className="flex justify-center items-center py-2">
//                           {showLoadingAnimation ? (
//                             <svg
//                               className="animate-spin h-6 w-6 text-blue-600"
//                               xmlns="http://www.w3.org/2000/svg"
//                               fill="none"
//                               viewBox="0 0 24 24"
//                             >
//                               <circle
//                                 className="opacity-25"
//                                 cx="12"
//                                 cy="12"
//                                 r="10"
//                                 stroke="currentColor"
//                                 strokeWidth="4"
//                               ></circle>
//                               <path
//                                 className="opacity-75"
//                                 fill="currentColor"
//                                 d="M4 12a8 8 0 018-8v8H4z"
//                               ></path>
//                             </svg>
//                           ) : (
//                             <p>Loading...</p>
//                           )}
//                         </div>
//                       ) : (
//                         <div
//                           id={`category-products-${category._id}`}
//                           className="mt-4"
//                         >
//                           <div className={`grid ${getGridColumns()} gap-6`}>
//                             {products
//                               .filter((product) =>
//                                 product.category?.__id
//                                   ? product.category.__id === category._id
//                                   : product.category?._id === category._id
//                               )
//                               .slice(0, visibleCount)
//                               .map((product) => (
//                                 <div
//                                   key={product._id}
//                                   className={`bg-white rounded-lg ${cardStyles[productCardStyle]} overflow-hidden transition-transform hover:scale-105 ${productInfoStyles[productInfoPosition]}`}
//                                 >
//                                   <div
//                                     className={`relative ${
//                                       productInfoPosition === "below"
//                                         ? "w-full"
//                                         : "w-1/2"
//                                     } ${
//                                       productImageRatios[
//                                         productImageAspectRatio
//                                       ]
//                                     } cursor-pointer`}
//                                     onClick={() => handleProductClick(product)}
//                                   >
//                                     <LazyImage
//                                       src={product.images?.[0]?.data}
//                                       alt={product.name}
//                                       className="absolute inset-0 w-full h-full object-cover"
//                                     />
//                                   </div>
//                                   <div
//                                     className="p-4 cursor-pointer"
//                                     onClick={() => handleProductClick(product)}
//                                   >
//                                     {productDisplayFields.showName !==
//                                       false && (
//                                       <h3
//                                         className={`text-md font-bold text-gray-900 mb-2 text-${productTextAlignment}`}
//                                       >
//                                         {product.name}
//                                       </h3>
//                                     )}
//                                     <div
//                                       className={`flex justify-between items-center ${
//                                         productTextAlignment === "center"
//                                           ? "justify-center"
//                                           : productTextAlignment === "right"
//                                           ? "justify-end"
//                                           : ""
//                                       }`}
//                                     >
//                                       {productDisplayFields.showPrice !==
//                                         false &&
//                                         canSeePrice() && (
//                                           <div
//                                             className={`flex flex-col ${
//                                               productTextAlignment === "center"
//                                                 ? "items-center"
//                                                 : productTextAlignment ===
//                                                   "right"
//                                                 ? "items-end"
//                                                 : "items-start"
//                                             }`}
//                                           >
//                                             <span className="text-lg font-bold text-green-600">
//                                               {priceDisplayType === "tier" &&
//                                               product.pricingTiers &&
//                                               product.pricingTiers.length > 0
//                                                 ? `$${product.pricingTiers[
//                                                     product.pricingTiers
//                                                       .length - 1
//                                                   ].price.toFixed(2)}`
//                                                 : priceDisplayType ===
//                                                     "wholesale" &&
//                                                   product.basePrice
//                                                 ? `$${product.basePrice.toFixed(
//                                                     2
//                                                   )}`
//                                                 : `$${
//                                                     product.regularPrice
//                                                       ? product.regularPrice.toFixed(
//                                                           2
//                                                         )
//                                                       : product.basePrice.toFixed(
//                                                           2
//                                                         )
//                                                   }`}
//                                             </span>

//                                             {priceDisplayType === "tier" &&
//                                               product.pricingTiers &&
//                                               product.pricingTiers.length >
//                                                 0 && (
//                                                 <span className="text-xs text-blue-600">
//                                                   Tier price
//                                                 </span>
//                                               )}
//                                             {priceDisplayType ===
//                                               "wholesale" && (
//                                               <span className="text-xs text-blue-600">
//                                                 Wholesale price
//                                               </span>
//                                             )}
//                                             {priceDisplayType === "regular" && (
//                                               <span className="text-xs text-blue-600">
//                                                 Regular price
//                                               </span>
//                                             )}
//                                           </div>
//                                         )}
//                                       {showQuickAction && (
//                                         <button
//                                           style={{
//                                             boxShadow: "none",
//                                             border: "none",
//                                           }}
//                                           onClick={(e) => {
//                                             e.stopPropagation();
//                                             handleProductClick(product);
//                                           }}
//                                           className="text-gray-600 hover:text-blue-600 transition-colors"
//                                           title={
//                                             quickActionIcon === "customize"
//                                               ? "Customize"
//                                               : "Add to Cart"
//                                           }
//                                         >
//                                           {quickActionIcon === "customize" ? (
//                                             <svg
//                                               xmlns="http://www.w3.org/2000/svg"
//                                               className="h-6 w-6"
//                                               fill="none"
//                                               viewBox="0 0 24 24"
//                                               stroke="currentColor"
//                                             >
//                                               <path
//                                                 strokeLinecap="round"
//                                                 strokeLinejoin="round"
//                                                 strokeWidth={2}
//                                                 d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
//                                               />
//                                             </svg>
//                                           ) : (
//                                             <svg
//                                               xmlns="http://www.w3.org/2000/svg"
//                                               className="h-6 w-6"
//                                               fill="none"
//                                               viewBox="0 0 24 24"
//                                               stroke="currentColor"
//                                             >
//                                               <path
//                                                 strokeLinecap="round"
//                                                 strokeLinejoin="round"
//                                                 strokeWidth={2}
//                                                 d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
//                                               />
//                                             </svg>
//                                           )}
//                                         </button>
//                                       )}
//                                     </div>
//                                   </div>
//                                 </div>
//                               ))}
//                             {products.filter((product) =>
//                               product.category?.__id
//                                 ? product.category.__id === category._id
//                                 : product.category?._id === category._id
//                             ).length === 0 && (
//                               <p className="text-gray-600 col-span-full text-center py-4">
//                                 No products found for this category.
//                               </p>
//                             )}
//                           </div>
//                           {products.filter((product) =>
//                             product.category?.__id
//                               ? product.category.__id === category._id
//                               : product.category?._id === category._id
//                           ).length > visibleCount && (
//                             <div className="flex justify-center mt-8">
//                               <button
//                                 onClick={() => {
//                                   setIsLoadingMore(true);
//                                   setTimeout(() => {
//                                     setVisibleCount(
//                                       visibleCount + loadIncrementMobile
//                                     );
//                                     setIsLoadingMore(false);
//                                   }, 500);
//                                 }}
//                                 className="text-white px-7 py-3 rounded hover:opacity-90 transition-colors"
//                                 style={{ backgroundColor: loadMoreButtonColor }}
//                               >
//                                 {isLoadingMore ? (
//                                   showLoadingAnimation ? (
//                                     <svg
//                                       className="animate-spin h-5 w-5 text-white mx-auto"
//                                       xmlns="http://www.w3.org/2000/svg"
//                                       fill="none"
//                                       viewBox="0 0 24 24"
//                                     >
//                                       <circle
//                                         className="opacity-25"
//                                         cx="12"
//                                         cy="12"
//                                         r="10"
//                                         stroke="currentColor"
//                                         strokeWidth="4"
//                                       ></circle>
//                                       <path
//                                         className="opacity-75"
//                                         fill="currentColor"
//                                         d="M4 12a8 8 0 018-8v8H4z"
//                                       ></path>
//                                     </svg>
//                                   ) : (
//                                     "Loading..."
//                                   )
//                                 ) : (
//                                   loadMoreButtonText
//                                 )}
//                               </button>
//                             </div>
//                           )}
//                         </div>
//                       ))}
//                   </div>
//                 ))}
//           </div>
//         </>
//       ) : (
//         // Slider layout for many categories
//         <div className="relative">
//           <button
//             onClick={() => scroll("left")}
//             className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100"
//           >
//             <ChevronLeft size={24} />
//           </button>

//           <div
//             ref={categorySliderRef}
//             className="flex overflow-x-auto hide-scrollbar gap-6 px-12"
//             style={{ paddingTop: "15px" }}
//           >
//             {filteredCategories.map((category) => (
//               <div
//                 key={category._id}
//                 onClick={() => {
//                   setSelectedCategory(category._id);
//                   // window.history.pushState(
//                   //   {},
//                   //   "",
//                   //   `/cat/${
//                   //     category.slug ||
//                   //     category.name.toLowerCase().replace(/\s+/g, "-")
//                   //   }`
//                   // );
//                 }}
//                 className="flex-none cursor-pointer transition-transform hover:scale-105"
//               >
//                 <div
//                   className={`${sliderImageSizes[sliderImageSize]} ${
//                     categoryImageShape === "circle"
//                       ? "rounded-full"
//                       : "rounded-lg"
//                   } overflow-hidden border-4 mb-3`}
//                   style={{ borderColor: categoryBorderColor }}
//                 >
//                   <LazyImage
//                     src={category.image?.data}
//                     alt={category.name}
//                     className="w-full h-full object-cover"
//                   />
//                 </div>
//                 <p className="text-center font-medium text-gray-800">
//                   {category.name}
//                 </p>
//               </div>
//             ))}
//           </div>

//           <button
//             onClick={() => scroll("right")}
//             className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white p-2 rounded-full shadow-lg hover:bg-gray-100"
//           >
//             <ChevronRight size={24} />
//           </button>
//         </div>
//       )}

//       {/* Product Detail Popup */}
//       {showProductPopup && selectedProduct && (
//         <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-75 flex items-center justify-center p-4">
//           <div
//             className={`bg-white rounded-lg shadow-xl relative max-h-screen overflow-auto ${
//               // On mobile, override the size to be smaller regardless of the setting
//               windowWidth < 768
//                 ? "w-full max-w-xs"
//                 : productPopupSettings.popupSize === "small"
//                 ? "w-full max-w-lg"
//                 : productPopupSettings.popupSize === "medium"
//                 ? "w-full max-w-2xl"
//                 : productPopupSettings.popupSize === "large"
//                 ? "w-full max-w-4xl"
//                 : "w-full max-w-7xl h-full"
//             }`}
//           >
//             {/* Close button */}
//             <button
//               onClick={() => setShowProductPopup(false)}
//               style={{
//                 // boxShadow: "none"
//                 backgroundColor: "#444444",
//                 color: "white",
//                 borderRadius: "40px",
//                 padding: 5,
//               }}
//               className="absolute top-2 right-1 text-gray-500 hover:text-gray-700 z-10"
//             >
//               <svg
//                 xmlns="http://www.w3.org/2000/svg"
//                 className="h-6 w-6"
//                 fill="none"
//                 viewBox="0 0 24 24"
//                 stroke="currentColor"
//               >
//                 <path
//                   strokeLinecap="round"
//                   strokeLinejoin="round"
//                   strokeWidth={2}
//                   d="M6 18L18 6M6 6l12 12"
//                 />
//               </svg>
//             </button>

//             <div className="flex flex-col md:flex-row">
//               {/* Product images */}
//               {productPopupSettings.showGallery !== false && (
//                 <div className="md:w-1/2 p-4">
//                   <div className="bg-gray-100 rounded-lg overflow-hidden">
//                     {selectedProduct.images &&
//                     selectedProduct.images.length > 0 ? (
//                       <div className="relative">
//                         <LazyImage
//                           src={selectedProduct.images[0]?.data}
//                           alt={selectedProduct.name}
//                           className={`w-full h-auto object-contain aspect-square`}
//                         />
//                         {windowWidth < 768 && (
//                           <div
//                             className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 h-1/5 px-3 pt-2"
//                             style={{ pointerEvents: "none" }}
//                           >
//                             <h2
//                               className={` text-white mb-2 ${
//                                 // On mobile, override the size to be smaller regardless of the setting
//                                 windowWidth < 768
//                                   ? "text-md font-semibold"
//                                   : "text-2xl font-bold"
//                               }`}
//                             >
//                               {selectedProduct.name}
//                             </h2>
//                           </div>
//                         )}
//                       </div>
//                     ) : (
//                       <div className="w-full h-64 flex items-center justify-center bg-gray-200 text-gray-500">
//                         No image available
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               )}

//               {/* Product details */}
//               <div
//                 className={`${
//                   productPopupSettings.showGallery !== false
//                     ? "md:w-1/2"
//                     : "w-full"
//                 } p-6 pt-0`}
//               >
//                 {windowWidth >= 768 && (
//                   <h2 className="text-lg mt-8 font-semibold text-gray-600 mb-2                 ">
//                     {selectedProduct.name}
//                   </h2>
//                 )}
//                 {productPopupSettings.showDescription !== false &&
//                   selectedProduct.description && (
//                     <div className="mb-4">
//                       {/* <h3 className="font-medium text-gray-700 mb-1">
//                         Description
//                       </h3> */}
//                       <p
//                         style={{
//                           maxHeight: 200 + "px",
//                           overflowY: "auto",
//                         }}
//                         className={`text-gray-600 ${
//                           // On mobile, override the size to be smaller regardless of the setting
//                           windowWidth < 768
//                             ? "text-sm font-normal max-h-20 h-20"
//                             : " "
//                         }`}
//                       >
//                         {selectedProduct.description}
//                       </p>
//                     </div>
//                   )}

//                 {productPopupSettings.showNote !== false &&
//                   selectedProduct.note && (
//                     <div className="mb-4">
//                       <h3 className="font-medium text-gray-700 mb-1">Note</h3>
//                       <p className="text-gray-600 italic">
//                         {selectedProduct.note}
//                       </p>
//                     </div>
//                   )}

//                 {productPopupSettings.showAttributes !== false &&
//                   selectedProduct.attributes &&
//                   selectedProduct.attributes.length > 0 && (
//                     <div className="mb-6">
//                       <h3 className="font-medium text-gray-700 mb-2">
//                         Specifications
//                       </h3>
//                       <div className="grid grid-cols-2 gap-2">
//                         {selectedProduct.attributes.map((attr, index) => (
//                           <div key={index} className="flex flex-col">
//                             <span className="text-sm text-gray-500">
//                               {attr.name}
//                             </span>
//                             <span className="font-medium">{attr.value}</span>
//                           </div>
//                         ))}
//                       </div>
//                     </div>
//                   )}

//                 {productPopupSettings.showPrice !== false &&
//                   (canSeePrice() ? (
//                     <div
//                       className={`flex flex-col ${
//                         productTextAlignment === "center"
//                           ? "items-center"
//                           : productTextAlignment === "right"
//                           ? "items-end"
//                           : "items-start"
//                       }`}
//                     >
//                       {/* ${selectedProduct.pricingTiers && selectedProduct.pricingTiers.length > 0 ?
//                         selectedProduct.pricingTiers[selectedProduct.pricingTiers.length - 1].price.toFixed(2) :
//                         selectedProduct.basePrice.toFixed(2)} */}
//                       <span className="text-lg font-bold text-green-600">
//                         {priceDisplayType === "tier" &&
//                         selectedProduct.pricingTiers &&
//                         selectedProduct.pricingTiers.length > 0
//                           ? `$${selectedProduct.pricingTiers[
//                               selectedProduct.pricingTiers.length - 1
//                             ].price.toFixed(2)}`
//                           : priceDisplayType === "wholesale" &&
//                             selectedProduct.basePrice
//                           ? `$${selectedProduct.basePrice.toFixed(2)}`
//                           : `$${
//                               selectedProduct.regularPrice
//                                 ? selectedProduct.regularPrice.toFixed(2)
//                                 : selectedProduct.basePrice.toFixed(2)
//                             }`}
//                       </span>

//                       {/* {priceDisplayType === "tier" &&
//                         selectedProduct.pricingTiers &&
//                         selectedProduct.pricingTiers.length > 0 && (
//                           <div className="text-xs text-blue-600">
//                             <div>Tier pricing:</div>
//                             {selectedProduct.pricingTiers
//                               .sort((a, b) => a.minQuantity - b.minQuantity)
//                               .map((tier, idx) => (
//                                 <div key={idx} className="whitespace-nowrap">
//                                   {tier.maxQuantity
//                                     ? `${tier.minQuantity}-${
//                                         tier.maxQuantity
//                                       }: $${tier.price.toFixed(2)}`
//                                     : `${
//                                         tier.minQuantity
//                                       }+: $${tier.price.toFixed(2)}`}
//                                 </div>
//                               ))}
//                           </div>
//                         )} */}
//                       {priceDisplayType === "tier" &&
//                         selectedProduct.pricingTiers &&
//                         selectedProduct.pricingTiers.length > 0 && (
//                           <div className="text-xs text-blue-600">
//                             <span>Tier pricing: </span>
//                             <span className="inline-flex flex-wrap gap-x-2">
//                               {selectedProduct.pricingTiers
//                                 .sort((a, b) => a.minQuantity - b.minQuantity)
//                                 .map((tier, idx, array) => (
//                                   <span key={idx} className="whitespace-nowrap">
//                                     {tier.maxQuantity
//                                       ? `${tier.minQuantity}-${
//                                           tier.maxQuantity
//                                         }: $${tier.price.toFixed(2)}`
//                                       : `${
//                                           tier.minQuantity
//                                         }+: $${tier.price.toFixed(2)}`}
//                                     {idx < array.length - 1 ? "," : ""}
//                                   </span>
//                                 ))}
//                             </span>
//                           </div>
//                         )}
//                       {priceDisplayType === "wholesale" && (
//                         <span className="text-xs text-blue-600">
//                           Wholesale price
//                         </span>
//                       )}
//                       {priceDisplayType === "regular" && (
//                         <span className="text-xs text-blue-600">
//                           Regular price
//                         </span>
//                       )}
//                     </div>
//                   ) : (
//                     <div className="text-sm italic text-gray-600">
//                       {loginPromptMessage}
//                     </div>
//                   ))}

//                 {/* Action buttons */}
//                 {/* <div className="flex flex-wrap gap-4 mt-6">
//                   {productPopupSettings.showCustomizeButton !== false && (
//                     <button
//                       onClick={() => navigateToProduct(selectedProduct)}
//                       className="px-6 py-2 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition-colors"
//                     >
//                       Customize
//                     </button>
//                   )}

//                   {productPopupSettings.showAddToCart !== false && (
//                        <div className="mt-2 flex items-center justify-between">
//                        <div className="flex items-center border border-gray-200 rounded overflow-hidden">
//                          <button
//                            onClick={(e) => {
//                              e.stopPropagation();
//                              const currentQty = productQuantities[selectedProduct._id] || defaultQuantity;
//                              if (currentQty > 1) {
//                                setProductQuantities({
//                                  ...productQuantities,
//                                  [selectedProduct._id]: currentQty - 1
//                                });
//                              }
//                            }}
//                            className="px-2 py-1 bg-gray-50 text-gray-600 hover:bg-gray-100"
//                          >
//                            -
//                          </button>
//                          <input
//                            type="number"
//                            min="1"
//                            value={productQuantities[selectedProduct._id] || defaultQuantity}
//                            onChange={(e) => {
//                              e.stopPropagation();
//                              const newQty = parseInt(e.target.value, 10) || 1;
//                              setProductQuantities({
//                                ...productQuantities,
//                                [selectedProduct._id]: newQty
//                              });
//                            }}
//                            className="w-24 text-center border-x border-gray-200 py-1"
//                          />
//                          <button
//                            onClick={(e) => {
//                              e.stopPropagation();
//                              const currentQty = productQuantities[selectedProduct._id] || defaultQuantity;
//                              setProductQuantities({
//                                ...productQuantities,
//                                [selectedProduct._id]: currentQty + 1
//                              });
//                            }}
//                            className="px-2 py-1 bg-gray-50 text-gray-600 hover:bg-gray-100"
//                          >
//                            +
//                          </button>
//                     <button
//                       onClick={() => {
//                         handleAddToCart(selectedProduct);
//                         setShowProductPopup(false);
//                       }}
//                       className="px-6 py-2 bg-green-600 text-white font-medium rounded hover:bg-green-700 transition-colors"
//                     >
//                       Add to Cart
//                     </button>
//                     </div>
//                      </div>
//                   )}
//                 </div> */}
//                 {/* Action buttons */}
//                 <div className="flex flex-wrap gap-4 mt-6">
//                   {productPopupSettings.showCustomizeButton !== false && (
//                     <button
//                       onClick={() => navigateToProduct(selectedProduct)}
//                       className="px-6 py-2 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition-colors"
//                     >
//                       Customize
//                     </button>
//                   )}

//                   {productPopupSettings.showAddToCart !== false &&
//                     canAddToCart() && (
//                       <div className="mt-2 flex items-center justify-between">
//                         <div className="flex items-center border border-gray-200 rounded overflow-hidden">
//                           <button
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               const currentQty =
//                                 productQuantities[selectedProduct._id] ||
//                                 defaultQuantity;
//                               if (currentQty > 1) {
//                                 setProductQuantities({
//                                   ...productQuantities,
//                                   [selectedProduct._id]: currentQty - 1,
//                                 });
//                               }
//                             }}
//                             className="px-2 py-1 bg-gray-50 text-gray-600 hover:bg-gray-100"
//                           >
//                             -
//                           </button>
//                           <input
//                             type="number"
//                             min="1"
//                             value={
//                               productQuantities[selectedProduct._id] ||
//                               defaultQuantity
//                             }
//                             onChange={(e) => {
//                               e.stopPropagation();
//                               const newQty = parseInt(e.target.value, 10) || 1;
//                               setProductQuantities({
//                                 ...productQuantities,
//                                 [selectedProduct._id]: newQty,
//                               });
//                             }}
//                             className="w-20 pl-0 text-center border-x border-gray-200 py-1"
//                           />
//                           <button
//                             onClick={(e) => {
//                               e.stopPropagation();
//                               const currentQty =
//                                 productQuantities[selectedProduct._id] ||
//                                 defaultQuantity;
//                               setProductQuantities({
//                                 ...productQuantities,
//                                 [selectedProduct._id]: currentQty + 1,
//                               });
//                             }}
//                             className="px-2 py-1 bg-gray-50 text-gray-600 hover:bg-gray-100"
//                           >
//                             +
//                           </button>
//                           <button
//                             onClick={() => {
//                               handleAddToCart(selectedProduct);
//                               setShowProductPopup(false);
//                             }}
//                             className="px-1 py-2 bg-green-600 text-white font-medium rounded hover:bg-green-700 transition-colors"
//                           >
//                             Add to Cart
//                           </button>
//                         </div>
//                       </div>
//                     )}
//                   {/* {productPopupSettings.showAddToCart !== false && !canAddToCart() && (
//                     <div className="mt-2 text-sm italic text-gray-600">{loginPromptMessage}</div>
//                   )} */}
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Login Prompt Notification */}
//       {showLoginPrompt && (
//         <div className="fixed inset-0 flex items-center justify-center z-50 transition-all duration-300 ease-in-out animate-notification">
//           <div className="bg-blue-500 text-white px-6 py-4 rounded-lg shadow-lg flex flex-col items-center space-y-4">
//             <p className="text-xl">{loginPromptMessage}</p>
//             <div className="flex gap-4">
//               <button
//                 onClick={() => (window.location.href = "/login")}
//                 className="px-4 py-2 bg-white text-blue-600 rounded hover:bg-gray-100"
//               >
//                 Login
//               </button>
//               <button
//                 onClick={() => setShowLoginPrompt(false)}
//                 className="px-4 py-2 border border-white text-white rounded hover:bg-blue-600"
//               >
//                 Close
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       <style>{`
//         .hide-scrollbar::-webkit-scrollbar {
//           display: none;
//         }
//         .hide-scrollbar {
//           -ms-overflow-style: none;
//           scrollbar-width: none;
//         }

//         @keyframes fadeIn {
//           from { opacity: 0; transform: translateX(-20px); }
//           to { opacity: 1; transform: translateX(0); }
//         }
//         .fade-in {
//           animation: fadeIn 0.5s ease-in-out;
//         }

//         @keyframes notification-animation {
//           0% { opacity: 0; transform: scale(0.8); }
//           10% { opacity: 1; transform: scale(1.1); }
//           20% { transform: scale(1); }
//           80% { opacity: 1; transform: scale(1); }
//           100% { opacity: 0; transform: scale(0.8); }
//         }

//         .animate-notification {
//           animation: notification-animation 2s ease-in-out forwards;
//         }
//         .category-item:hover {
//           background-color: ${categoryHoverBackgroundColor} !important;
//           color: ${categoryHoverTextColor} !important;
//         }
//         @keyframes login-prompt-animation {
//           0% { opacity: 0; transform: scale(0.8); }
//           10% { opacity: 1; transform: scale(1.05); }
//           20% { transform: scale(1); }
//           80% { opacity: 1; transform: scale(1); }
//           100% { opacity: 0; transform: scale(0.8); }
//         }

//         .animate-login-prompt {
//           animation: login-prompt-animation 3s ease-in-out forwards;
//         }
//           .product-grid-item {
//   display: flex;
//   flex-direction: column;
// }

// .product-image-container {
//   position: relative;
//   width: 100%;
//   padding-bottom: 100%; /* 1:1 Aspect Ratio by default */
// }

// .product-image-container.portrait {
//   padding-bottom: 133.33%; /* 3:4 Aspect Ratio */
// }

// .product-image-container.landscape {
//   padding-bottom: 75%; /* 4:3 Aspect Ratio */
// }

// .product-image {
//   position: absolute;
//   top: 0;
//   left: 0;
//   width: 100%;
//   height: 100%;
//   object-fit: cover;
// }
//       `}</style>
//     </div>
//   );
// };

// export default ProductCatalogVerticalSection;
